import React, { useCallback, useEffect, useMemo, useState } from "react";
import EnrichmentPreviewSourceEditor from "./EnrichmentPreviewSourceEditor";
import { createEnrichmentRuleDetail, getEnrichmentPreviewSourceFieldEdit } from "../utils/enrichmentPreview";
import Editor from "@monaco-editor/react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tab,
  TextField as MuiTextField,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";
import HistoryOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PublicOutlinedIcon from "@mui/icons-material/Public";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import axiosInstance from "../axiosInstance";
import axiosInstanceImporter from "../axiosInstanceImporter";
import { isAdminUser, isFullAdmin } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";
import { extractDocxPreview } from "../utils/docxPreview";
import AssetPickerDialog from "./AssetPickerDialog";
import CacheV2Section from "./CacheV2Section";
import ProductDiscoverySection from "./ProductDiscoverySection";
import OrderTrackingSettings from "./OrderTrackingSettings";
import ShopifyClientCredentials from "./ShopifyClientCredentials";
import { normalizeImportSettings } from "../utils/importSettings";
import NightlyProductUpdateToggle from "./NightlyProductUpdateToggle";
import TenantTaxonomySection from "./TenantTaxonomySection";
import { SHOW_SHOPIFY_TAXONOMY } from "../config/featureFlags";

const PREEZIE_ROOT_TENANT_ID = "tnt_IjvUGLPhovxmKGs";

const ASSET_FILE_ACCEPT = [
  "image/*",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/json",
  ".xls",
  ".xlsx",
  ".doc",
  ".docx",
].join(",");

const getPublicOfficePreviewUrl = (asset) => {
  const isPublic = asset?.visibility?.toLowerCase() === "public";
  return isPublic && asset?.url
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(asset.url)}`
    : "";
};

const pathToSegments = (path) => path.split(".").filter(Boolean);

const cloneWithUpdatedValue = (source, path, nextValue) => {
  const segments = pathToSegments(path);
  if (segments.length === 0) return source;

  const draft = Array.isArray(source) ? [...source] : { ...source };
  let cursor = draft;

  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      cursor[segment] = nextValue;
      return;
    }

    const currentValue = cursor[segment];
    cursor[segment] = Array.isArray(currentValue) ? [...currentValue] : { ...(currentValue || {}) };
    cursor = cursor[segment];
  });

  return draft;
};

const isBooleanLike = (value) =>
  typeof value === "boolean" ||
  (typeof value === "string" && ["true", "false"].includes(value.toLowerCase()));

const toBoolean = (value) =>
  typeof value === "boolean" ? value : String(value).toLowerCase() === "true";

const isHexColor = (value) => typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);

const isNumericValue = (value) =>
  typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value)));

const importerMerchandisingConfigKeys = ["EnableProductSalesCount", "ProductSalesLookbackDays"];

const isJsonString = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

const prettifyKey = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const matchesSearch = (label, value, query) => {
  if (!query.trim()) return true;
  const haystack = `${label} ${value == null ? "" : typeof value === "string" ? value : JSON.stringify(value)}`
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
};

const countEntries = (value) => {
  if (value == null) return 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value !== "object") return 1;
  return Object.keys(value).length;
};

const normaliseMetafieldDefinitionGroups = (groupsOrMetafields) => {
  const entries = Array.isArray(groupsOrMetafields) ? groupsOrMetafields : [];
  const groups = [];

  const getExampleValue = (metafield) => {
    if (metafield?.value !== undefined) return metafield.value;
    if (!Array.isArray(metafield?.values)) return metafield?.values;

    const firstValue = metafield.values[0];
    return firstValue && typeof firstValue === "object" && "value" in firstValue
      ? firstValue.value
      : firstValue;
  };

  const addMetafield = (namespace, metafield) => {
    const key = String(typeof metafield === "string" ? metafield : metafield?.key || "").trim();
    if (!namespace || !key) return;

    const group = groups.find((item) => item.namespace === namespace);
    const normalisedMetafield = {
      key,
      value: typeof metafield === "string" ? undefined : getExampleValue(metafield),
    };
    if (group) group.metafields.push(normalisedMetafield);
    else groups.push({ namespace, metafields: [normalisedMetafield] });
  };

  const processEntries = (items, inheritedNamespace = "") => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (typeof item === "string") {
        addMetafield(inheritedNamespace, item);
        return;
      }

      const namespace = String(item?.namespace || inheritedNamespace || "").trim();
      if (Array.isArray(item?.metafields)) {
        processEntries(item.metafields, namespace);
      } else {
        addMetafield(namespace, item);
      }
    });
  };

  processEntries(entries);
  return groups;
};

const mergeMetafieldGroups = (definitionGroups, valueGroups) => {
  const groups = normaliseMetafieldDefinitionGroups(definitionGroups).map((group) => ({
    ...group,
    metafields: group.metafields.map((metafield) => ({ ...metafield })),
  }));

  normaliseMetafieldDefinitionGroups(valueGroups).forEach((valueGroup) => {
    const group = groups.find((item) => item.namespace === valueGroup.namespace);
    if (!group) {
      groups.push(valueGroup);
      return;
    }

    valueGroup.metafields.forEach((valueMetafield) => {
      const metafield = group.metafields.find((item) => item.key === valueMetafield.key);
      if (metafield) {
        if (valueMetafield.value !== undefined) metafield.value = valueMetafield.value;
      } else {
        group.metafields.push(valueMetafield);
      }
    });
  });

  return groups;
};

const getExampleVariantMetafields = (responseData) => {
  const variantEntries = responseData?.variantMetafields ?? responseData?.variants;
  if (!Array.isArray(variantEntries) || variantEntries.length === 0) return variantEntries;

  const firstVariant = variantEntries[0];
  if (Array.isArray(firstVariant)) return firstVariant;
  // Definitions are already grouped by namespace. Actual variant values are
  // grouped by variant, so use the first variant as the display example.
  return !firstVariant?.namespace && Array.isArray(firstVariant?.metafields)
    ? firstVariant.metafields
    : variantEntries;
};

const formatMetafieldValue = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const ProductMetafieldPreviewJson = ({ value }) => {
  let inVariants = false;
  const lines = JSON.stringify(value, null, 2).split("\n");

  return lines.map((line, index) => {
    const isVariantsProperty = line.startsWith('  "variants":');
    if (isVariantsProperty) inVariants = true;

    const isVariantLine = inVariants;
    const closesVariants = inVariants && line === "  ]";
    if (closesVariants) inVariants = false;

    return (
      <React.Fragment key={`${line}-${index}`}>
        <Box component="span" sx={{ color: isVariantLine ? "#7dd3fc" : "#fdba74" }}>
          {line}
        </Box>
        {index < lines.length - 1 && "\n"}
      </React.Fragment>
    );
  });
};

const toStoredMetafieldValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.includes("__")) return trimmed;
  const separatorIndex = trimmed.indexOf(".");
  return separatorIndex === -1
    ? trimmed
    : `${trimmed.slice(0, separatorIndex)}__${trimmed.slice(separatorIndex + 1)}`;
};

const parseMetafieldValues = (value) =>
  [...new Set(
    (Array.isArray(value) ? value : String(value || "").split(","))
      .map(toStoredMetafieldValue)
      .filter(Boolean)
  )];

const displayMetafieldValue = (value) => {
  const separatorIndex = value.indexOf("__");
  return separatorIndex === -1
    ? value
    : `${value.slice(0, separatorIndex)}.${value.slice(separatorIndex + 2)}`;
};

const parseHotlinks = (rawValue) => {
  if (!rawValue || typeof rawValue !== "string") return [];
  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const sectionPaperSx = {
  borderRadius: "24px",
  border: "1px solid rgba(255, 132, 76, 0.12)",
  backgroundColor: "rgba(255,255,255,0.92)",
  boxShadow: "0 16px 36px rgba(236, 123, 55, 0.08)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: "rgba(15, 23, 42, 0.10)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(249, 115, 22, 0.34)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#fb923c",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#f97316",
  },
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const temporaryInput = document.createElement("textarea");
  temporaryInput.value = text;
  temporaryInput.style.position = "fixed";
  temporaryInput.style.opacity = "0";
  document.body.appendChild(temporaryInput);
  temporaryInput.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } finally {
    temporaryInput.remove();
  }

  if (!copied) {
    throw new Error("Copy command was unsuccessful");
  }
};

function OverflowValueTooltip({ children, value, contentSelector }) {
  const [open, setOpen] = useState(false);
  const tooltipValue = value == null ? "" : String(value);

  const handleMouseEnter = (event) => {
    const content = event.currentTarget.querySelector(contentSelector);
    const isOverflowing =
      content &&
      (content.scrollWidth > content.clientWidth + 1 ||
        content.scrollHeight > content.clientHeight + 1);

    setOpen(Boolean(tooltipValue) && Boolean(isOverflowing));
  };

  return (
    <Tooltip
      open={open}
      title={tooltipValue}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            whiteSpace: "nowrap",
            fontSize: "0.95rem",
            lineHeight: 1.4,
            px: 1.5,
            py: 1,
          },
        },
      }}
    >
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setOpen(false)}
        sx={{ width: "100%" }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

const TextField = React.forwardRef(function TextField(
  { InputProps = {}, inputProps = {}, label, disableValueTooltip = false, ...props },
  ref
) {
  const [copyTooltip, setCopyTooltip] = useState("Copy value");
  const copyTooltipTimeout = React.useRef(null);

  useEffect(
    () => () => {
      if (copyTooltipTimeout.current) {
        window.clearTimeout(copyTooltipTimeout.current);
      }
    },
    []
  );

  const showCopyTooltip = (message) => {
    if (copyTooltipTimeout.current) {
      window.clearTimeout(copyTooltipTimeout.current);
    }
    setCopyTooltip(message);
    copyTooltipTimeout.current = window.setTimeout(
      () => setCopyTooltip("Copy value"),
      1200
    );
  };

  const handleCopyValue = async (event) => {
    const input = event.currentTarget
      .closest(".MuiInputBase-root")
      ?.querySelector("input, textarea");

    try {
      await copyTextToClipboard(input?.value ?? "");
      showCopyTooltip("Copied");
    } catch {
      showCopyTooltip("Unable to copy");
    }
  };

  const copyLabel = typeof label === "string" ? `Copy ${label}` : "Copy value";
  const tooltipValue = inputProps.value ?? props.value ?? props.defaultValue ?? "";

  return (
    <OverflowValueTooltip value={disableValueTooltip ? "" : tooltipValue} contentSelector="input, textarea">
      <MuiTextField
        {...props}
        ref={ref}
        label={label}
        inputProps={inputProps}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <>
              {InputProps.endAdornment}
              <InputAdornment position="end">
                <Tooltip title={copyTooltip}>
                  <IconButton
                    aria-label={copyLabel}
                    edge="end"
                    size="small"
                    onClick={handleCopyValue}
                    onMouseDown={(event) => event.preventDefault()}
                    sx={{ color: "#94a3b8", "&:hover": { color: "#f97316" } }}
                  >
                    <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            </>
          ),
        }}
      />
    </OverflowValueTooltip>
  );
});

const metaChipSx = {
  borderRadius: "999px",
  fontWeight: 700,
  px: 0.75,
  backgroundColor: "rgba(255, 247, 241, 0.92)",
  border: "1px solid rgba(255, 132, 76, 0.14)",
  color: "#ea580c",
};

function ConfigField({
  label,
  path,
  value,
  onValueChange,
  searchQuery,
  helperText,
  minRows = 3,
  forceSingleLine = false,
  options,
  disabled = false,
  inputProps,
}) {
  if (!matchesSearch(label, value, searchQuery)) {
    return null;
  }

  const handleChange = (nextValue) => {
    onValueChange(path, nextValue);
  };

  if (options?.length) {
    const selectLabelId = `${path.replace(/\./g, "-")}-label`;
    const selectedOption = options.find(
      (option) =>
        option.value === value ||
        (typeof option.value === "string" &&
          typeof value === "string" &&
          option.value.toLowerCase() === value.toLowerCase())
    );
    const selectValue = selectedOption?.value ?? value;

    return (
      <OverflowValueTooltip
        value={selectedOption?.label ?? value}
        contentSelector=".MuiSelect-select"
      >
        <FormControl fullWidth disabled={disabled} sx={inputSx}>
          <InputLabel id={selectLabelId}>{label}</InputLabel>
          <Select
            labelId={selectLabelId}
            label={label}
            value={selectValue}
            onChange={(event) => handleChange(event.target.value)}
            MenuProps={{ disableScrollLock: true }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </OverflowValueTooltip>
    );
  }

  if (Array.isArray(value)) {
    return (
      <TextField
        fullWidth
        label={label}
        value={value.join(", ")}
        onChange={(event) =>
          handleChange(
            event.target.value
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          )
        }
        helperText={helperText || "Comma-separated values"}
        sx={inputSx}
      />
    );
  }

  if (isBooleanLike(value)) {
    const checked = toBoolean(value);
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: "18px",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          backgroundColor: "rgba(255,255,255,0.86)",
          height: "100%",
        }}
      >
        <FormControlLabel
          sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
          control={
            <Switch
              checked={checked}
              onChange={(event) =>
                handleChange(typeof value === "boolean" ? event.target.checked : String(event.target.checked))
              }
              color="warning"
            />
          }
          label={
            <Box sx={{ pr: 1 }}>
              <Typography sx={{ fontWeight: 700, color: "#202531" }}>{label}</Typography>
              {helperText ? (
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.3 }}>
                  {helperText}
                </Typography>
              ) : null}
            </Box>
          }
        />
      </Paper>
    );
  }

  if (isHexColor(value)) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid rgba(15, 23, 42, 0.10)",
            flexShrink: 0,
          }}
        >
          <Box
            component="input"
            type="color"
            value={value}
            onChange={(event) => handleChange(event.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              border: 0,
              padding: 0,
              backgroundColor: "transparent",
              cursor: "pointer",
            }}
          />
        </Box>
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          size="small"
          helperText={helperText}
          sx={inputSx}
        />
      </Stack>
    );
  }

  const useMultiline =
    !forceSingleLine &&
    typeof value === "string" &&
    (value.length > 60 || value.includes("\n") || isJsonString(value) || label.toLowerCase().includes("style"));

  const fieldValue =
    value == null ? "" : typeof value === "string" ? value : typeof value === "number" ? String(value) : JSON.stringify(value, null, 2);

  return (
    <TextField
      fullWidth
      label={label}
      value={fieldValue}
      type={!useMultiline && isNumericValue(value) ? "number" : "text"}
      multiline={useMultiline}
      minRows={useMultiline ? minRows : undefined}
      onChange={(event) => {
        const nextRawValue = event.target.value;
        if (value == null || typeof value === "string") {
          handleChange(nextRawValue);
          return;
        }
        if (typeof value === "number") {
          handleChange(nextRawValue === "" ? 0 : Number(nextRawValue));
          return;
        }
        try {
          handleChange(JSON.parse(nextRawValue));
        } catch {
          handleChange(nextRawValue);
        }
      }}
      helperText={helperText}
      inputProps={inputProps}
      sx={inputSx}
    />
  );
}

function DynamicFieldGrid({ objectValue, basePath, excludeKeys = [], searchQuery, onValueChange, compact = false }) {
  const entries = Object.entries(objectValue || {}).filter(([key]) => !excludeKeys.includes(key));
  if (entries.length === 0) return null;

  return (
    <Grid container spacing={compact ? 1.5 : 2} sx={compact ? { "& .MuiInputLabel-root": { fontSize: "0.85rem" } } : undefined}>
      {entries.map(([key, value]) => (
        <Grid item xs={12} md={compact ? 4 : 6} key={key}>
          <ConfigField
            label={prettifyKey(key)}
            path={`${basePath}.${key}`}
            value={value}
            onValueChange={onValueChange}
            searchQuery={searchQuery}
          />
        </Grid>
      ))}
    </Grid>
  );
}

function ExpandableFieldGrid({ objectValue, basePath, excludeKeys = [], searchQuery, onValueChange }) {
  const [expandedField, setExpandedField] = useState(null);
  const entries = Object.entries(objectValue || {}).filter(([key]) => !excludeKeys.includes(key));
  if (entries.length === 0) return null;

  const isSimpleValue = (value) => {
    if (value == null) return true;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (Array.isArray(value)) return true;
    if (isBooleanLike(value)) return true;
    if (typeof value === "string" && value.length <= 80 && !value.includes("\n") && !isJsonString(value)) return true;
    return false;
  };

  return (
    <>
      <Grid container spacing={2}>
        {entries.map(([key, value]) => {
          const label = prettifyKey(key);
          const path = `${basePath}.${key}`;
          if (!matchesSearch(label, value, searchQuery)) return null;

          if (isSimpleValue(value)) {
            return (
              <Grid item xs={12} md={6} key={key}>
                <ConfigField
                  label={label}
                  path={path}
                  value={value}
                  onValueChange={onValueChange}
                  searchQuery={searchQuery}
                />
              </Grid>
            );
          }

          const fieldValue =
            value == null ? "" : typeof value === "string" ? value : typeof value === "number" ? String(value) : JSON.stringify(value, null, 2);

          return (
            <Grid item xs={12} md={6} key={key}>
              <TextField
                fullWidth
                label={label}
                value={fieldValue}
                multiline
                minRows={4}
                maxRows={4}
                onChange={(event) => {
                  const nextRawValue = event.target.value;
                  if (value == null || typeof value === "string") {
                    onValueChange(path, nextRawValue);
                    return;
                  }
                  if (typeof value === "number") {
                    onValueChange(path, nextRawValue === "" ? 0 : Number(nextRawValue));
                    return;
                  }
                  try {
                    onValueChange(path, JSON.parse(nextRawValue));
                  } catch {
                    onValueChange(path, nextRawValue);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                      <Tooltip title="Expand">
                        <IconButton size="small" onClick={() => setExpandedField({ key, label, path, value: fieldValue })}>
                          <OpenInFullIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={!!expandedField}
        onClose={() => setExpandedField(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            boxShadow: "0 24px 60px rgba(236, 123, 55, 0.14)",
          },
        }}
      >
        {expandedField && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: "#202531" }}>{expandedField.label}</Typography>
              <IconButton onClick={() => setExpandedField(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
              <TextField
                fullWidth
                multiline
                minRows={12}
                value={
                  (() => {
                    const current = objectValue?.[expandedField.key];
                    return current == null ? "" : typeof current === "string" ? current : typeof current === "number" ? String(current) : JSON.stringify(current, null, 2);
                  })()
                }
                onChange={(event) => {
                  const raw = event.target.value;
                  const originalValue = objectValue?.[expandedField.key];
                  if (originalValue == null || typeof originalValue === "string") {
                    onValueChange(expandedField.path, raw);
                    return;
                  }
                  if (typeof originalValue === "number") {
                    onValueChange(expandedField.path, raw === "" ? 0 : Number(raw));
                    return;
                  }
                  try {
                    onValueChange(expandedField.path, JSON.parse(raw));
                  } catch {
                    onValueChange(expandedField.path, raw);
                  }
                }}
                sx={inputSx}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}

function MetafieldPanel({ title, definitionGroups, isLoading, error, onRetry, value, onValueChange, searchQuery, requiredValues = [] }) {
  const [filter, setFilter] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [customMetafields, setCustomMetafields] = useState([]);
  const [customMetafieldMessage, setCustomMetafieldMessage] = useState(null);
  const selectedValues = parseMetafieldValues(value);
  const activeFilter = (filter || searchQuery || "").trim().toLowerCase();

  const groupsWithCustomMetafields = useMemo(() => {
    const definedValues = new Set(
      definitionGroups.flatMap((group) => group.metafields.map((metafield) => `${group.namespace}__${metafield.key}`))
    );
    const customGroups = [];

    [...new Set([...customMetafields, ...selectedValues])]
      .filter((storedValue) => !definedValues.has(storedValue))
      .forEach((storedValue) => {
        const separatorIndex = storedValue.indexOf("__");
        if (separatorIndex <= 0 || separatorIndex === storedValue.length - 2) return;

        const namespace = storedValue.slice(0, separatorIndex);
        const key = storedValue.slice(separatorIndex + 2);
        const group = customGroups.find((item) => item.namespace === namespace);
        if (group) group.metafields.push({ key });
        else customGroups.push({ namespace, metafields: [{ key }] });
      });

    return [...definitionGroups, ...customGroups];
  }, [customMetafields, definitionGroups, selectedValues]);

  const filteredGroups = groupsWithCustomMetafields
    .map((group) => ({
      ...group,
      metafields: group.metafields.filter((metafield) =>
        !activeFilter || `${group.namespace}.${metafield.key} ${formatMetafieldValue(metafield.value) || ""}`.toLowerCase().includes(activeFilter)
      ),
    }))
    .filter((group) => group.metafields.length > 0);

  const updateSelections = (nextValues) => onValueChange([...new Set([...nextValues, ...requiredValues])].join(","));

  const toggleMetafield = (namespace, key) => {
    const storedValue = `${namespace}__${key}`;
    if (requiredValues.includes(storedValue)) return;
    updateSelections(
      selectedValues.includes(storedValue)
        ? selectedValues.filter((item) => item !== storedValue)
        : [...selectedValues, storedValue]
    );
  };

  const addCustomMetafields = () => {
    const customValues = parseMetafieldValues(customValue).filter((storedValue) => {
      const separatorIndex = storedValue.indexOf("__");
      return separatorIndex > 0 && separatorIndex < storedValue.length - 2;
    });
    if (customValues.length === 0) {
      setCustomMetafieldMessage({ type: "error", text: "Enter a metafield in namespace__key format." });
      return;
    }
    updateSelections([...selectedValues, ...customValues]);
    setCustomMetafields((previous) => [...new Set([...previous, ...customValues])]);
    setCustomMetafieldMessage({
      type: "success",
      text: `Added ${customValues.length} custom metafield${customValues.length === 1 ? "" : "s"}.`,
    });
    setCustomValue("");
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: "20px",
        border: "1px solid rgba(255, 132, 76, 0.14)",
        backgroundColor: "rgba(255,255,255,0.82)",
        height: "100%",
      }}
    >
      <Typography sx={{ fontWeight: 800, color: "#202531", fontSize: "1.1rem", mb: 1.5 }}>{title}</Typography>
      {requiredValues.length > 0 && (
        <Typography variant="caption" sx={{ display: "block", color: "#6b7280", mb: 1 }}>
          Metafields selected in Sizing Information or Size Chart Metafields are required here. Remove those selections first to remove the product entry.
        </Typography>
      )}
      <TextField
        fullWidth
        size="small"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Search metafields"
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 19 }} /></InputAdornment> }}
        sx={{ ...inputSx, mb: 1.5 }}
      />

      {isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
          <CircularProgress size={20} color="warning" />
          <Typography variant="body2" sx={{ color: "#6b7280" }}>Loading metafields…</Typography>
        </Stack>
      ) : error ? (
        <Stack spacing={1} alignItems="center" sx={{ py: 2.5 }}>
          <Typography variant="body2" sx={{ color: "#b91c1c", textAlign: "center" }}>{error}</Typography>
          <Button size="small" variant="outlined" onClick={onRetry} sx={{ textTransform: "none" }}>Retry</Button>
        </Stack>
      ) : (
      <Stack spacing={1}>
        {filteredGroups.map((group) => (
          <Accordion
            key={group.namespace}
            elevation={0}
            defaultExpanded={group.metafields.some((metafield) => selectedValues.includes(`${group.namespace}__${metafield.key}`))}
            disableGutters
            sx={{
              overflow: "hidden",
              borderRadius: "14px !important",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
              sx={{
                px: 1.5,
                minHeight: 48,
                backgroundColor: "rgba(255,247,241,0.72)",
                "& .MuiAccordionSummary-content": { my: 1, minWidth: 0 },
                "& .MuiAccordionSummary-expandIconWrapper": { flexShrink: 0 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%", minWidth: 0, pr: 1 }}>
              <Typography
                sx={{
                  minWidth: 0,
                  flex: 1,
                  fontWeight: 700,
                  color: "#202531",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {group.namespace}
              </Typography>
              <Chip
                label={`${group.metafields.filter((metafield) => selectedValues.includes(`${group.namespace}__${metafield.key}`)).length} selected`}
                size="small"
                sx={{ ...metaChipSx, flexShrink: 0, fontSize: "0.7rem", height: 22 }}
              />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
            {group.metafields.map((metafield) => {
              const storedValue = `${group.namespace}__${metafield.key}`;
              const formattedValue = formatMetafieldValue(metafield.value);
              return (
                <Box key={storedValue} sx={{ px: 1, borderTop: "1px solid rgba(15, 23, 42, 0.06)", backgroundColor: selectedValues.includes(storedValue) ? "rgba(255, 241, 233, 0.62)" : "#fff" }}>
                  <FormControlLabel
                    sx={{ m: 0, width: "100%", py: 0.25 }}
                    control={
                      <Checkbox checked={selectedValues.includes(storedValue)} disabled={requiredValues.includes(storedValue)} onChange={() => toggleMetafield(group.namespace, metafield.key)} color="warning" />
                    }
                    label={
                      <Box sx={{ minWidth: 0, py: 0.35 }}>
                        <Typography sx={{ color: "#374151", fontSize: "0.88rem", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                          {group.namespace}.{metafield.key}
                        </Typography>
                        {formattedValue && (
                          <Tooltip title={formattedValue}>
                            <Typography variant="caption" noWrap sx={{ display: "block", maxWidth: 320, color: "#6b7280" }}>
                              Value: {formattedValue}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Box>
              );
            })}
            </AccordionDetails>
          </Accordion>
        ))}
        {filteredGroups.length === 0 && (
          <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 2 }}>No metafields match this search.</Typography>
        )}
      </Stack>
      )}

      <Box sx={{ pt: 2, mt: 2, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
        <Typography sx={{ fontWeight: 700, color: "#202531", mb: 1 }}>Add custom metafield</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            value={customValue}
            onChange={(event) => setCustomValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addCustomMetafields();
              }
            }}
            placeholder="namespace__key"
            sx={inputSx}
          />
          <Button
            variant="outlined"
            onClick={addCustomMetafields}
            sx={{ minWidth: 72, borderColor: "rgba(249, 115, 22, 0.35)", color: "#ea580c", borderRadius: "12px", textTransform: "none", fontWeight: 700 }}
          >
            Add
          </Button>
        </Stack>
        <Typography variant="caption" sx={{ display: "block", color: "#6b7280", mt: 0.75 }}>
          Use <Box component="span" sx={{ fontFamily: "monospace" }}>namespace__key</Box>
          ; comma-separated values are supported.
        </Typography>
        {customMetafieldMessage && (
          <Typography role={customMetafieldMessage.type === "error" ? "alert" : "status"} variant="caption" sx={{ display: "block", color: customMetafieldMessage.type === "error" ? "#b91c1c" : "#15803d", mt: 0.5 }}>
            {customMetafieldMessage.text}
          </Typography>
        )}
      </Box>

      {selectedValues.length > 0 && (
        <Box sx={{ pt: 2, mt: 2, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
          <Typography sx={{ fontWeight: 700, color: "#202531", mb: 1 }}>Selected ({selectedValues.length})</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {selectedValues.map((storedValue) => (
              <Chip
                key={storedValue}
                label={displayMetafieldValue(storedValue)}
                onDelete={requiredValues.includes(storedValue) ? undefined : () => updateSelections(selectedValues.filter((item) => item !== storedValue))}
                sx={{
                  ...metaChipSx,
                  maxWidth: "100%",
                  height: 28,
                  "& .MuiChip-label": { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

function PreviewWidgetDialog({ open, onClose, tenantDetails }) {
  const tenantId = tenantDetails?.id || "";

  React.useEffect(() => {
    if (open && tenantId) {
      const widgetUrl = `${process.env.REACT_APP_WIDGET_URL}/?tid=${tenantId}&preezie-shop-assistant-preview=true&app_type=pdp`;
      window.open(widgetUrl, "_blank", "noopener,noreferrer");
      onClose();
    } else if (open && !tenantId) {
      onClose();
    }
  }, [open, tenantId, onClose]);

  return null;
}

function SectionView({ title, description, count, showCount = true, icon: Icon, action, children }) {
  return (
    <Paper elevation={0} sx={{ ...sectionPaperSx, p: { xs: 2, md: 2.5 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2.25 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {Icon && <Icon sx={{ fontSize: "1.8rem", color: "#f97316" }} />}
            <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "#202531" }}>
              {title}
            </Typography>
            {showCount && <Chip label={count} size="small" sx={metaChipSx} />}
          </Stack>
          <Typography sx={{ color: "#6b7280", mt: 0.55 }}>
            {description}
          </Typography>
        </Box>
        {action ? <Box>{action}</Box> : null}
      </Stack>
      {children}
    </Paper>
  );
}

const TenantManagementModernLayout = ({
  tenantDetails,
  setTenantDetails,
  handleSaveTenant,
  onShopifyCredentialsSaved,
  savingTenant,
  jsonError,
  handleOpenCsvPreview,
  handleClearTenantCache,
  clearingTenantCache,
  primaryButtonSx,
  neutralButtonSx,
  importSettings,
  setImportSettings,
  isImportSettingsDirty,
  setIsImportSettingsDirty,
  availableFieldNames,
  handleImportSettingTextFieldChange,
  handleHasParentProductChange,
  handleUsePreezieGeneratedIdChange,
  handleUseAggregationValueAsDocumentIdChange,
  handleRuleChange,
  handleAddRule,
  handleRemoveRule,
  handleResetImportSettings,
  handleSaveImportSettings,
  isAggregationRulePresent,
  handleTestRules,
  testRulesProductId,
  setTestRulesProductId,
  testRulesLoading,
  testRulesResults,
  testRulesRawProduct,
  handleRetestRule,
  ruleDetailRetesting,
  ruleDetailOpen,
  setRuleDetailOpen,
  ruleDetailData,
  setRuleDetailData,
  activeSection,
  setActiveSection,
  expandedGroups,
  setExpandedGroups,
}) => {
  const isAdmin = isAdminUser(getUserEmail());
  const canManageAssets = tenantDetails?.id !== PREEZIE_ROOT_TENANT_ID || isAdmin;
  const [searchQuery, setSearchQuery] = useState("");
  const [savingNightlyProductUpdate, setSavingNightlyProductUpdate] = useState(false);
  const [tenantJsonDraft, setTenantJsonDraft] = useState(() => JSON.stringify(tenantDetails, null, 2));
  const [tenantJsonError, setTenantJsonError] = useState(null);
  const [tenantJsonEditorReady, setTenantJsonEditorReady] = useState(false);
  const [tenantJsonSearch, setTenantJsonSearch] = useState("");
  const [tenantJsonMatches, setTenantJsonMatches] = useState([]);
  const [tenantJsonMatchIndex, setTenantJsonMatchIndex] = useState(0);
  const tenantJsonEditorRef = React.useRef(null);
  const tenantJsonSearchDecorationsRef = React.useRef(null);
  const lastAppliedTenantJsonRef = React.useRef(tenantDetails);

  useEffect(() => {
    if (tenantDetails === lastAppliedTenantJsonRef.current) return;
    lastAppliedTenantJsonRef.current = tenantDetails;
    setTenantJsonDraft(JSON.stringify(tenantDetails, null, 2));
    setTenantJsonError(null);
  }, [tenantDetails]);

  useEffect(() => {
    if (activeSection === "tenant-json") return;
    tenantJsonEditorRef.current = null;
    tenantJsonSearchDecorationsRef.current = null;
    setTenantJsonEditorReady(false);
  }, [activeSection]);

  useEffect(() => {
    const model = tenantJsonEditorReady && activeSection === "tenant-json"
      ? tenantJsonEditorRef.current?.getModel()
      : null;
    setTenantJsonMatches(model && tenantJsonSearch
      ? model.findMatches(tenantJsonSearch, false, false, false, null, false)
      : []);
    setTenantJsonMatchIndex(0);
  }, [tenantJsonSearch, tenantJsonDraft, tenantJsonEditorReady, activeSection]);

  useEffect(() => {
    const editor = tenantJsonEditorRef.current;
    if (!editor || !tenantJsonSearchDecorationsRef.current) return;
    tenantJsonSearchDecorationsRef.current.set(tenantJsonMatches.map((match, index) => ({
      range: match.range,
      options: { inlineClassName: index === tenantJsonMatchIndex ? "tenant-json-current-match" : "tenant-json-search-match" },
    })));
    const match = tenantJsonMatches[tenantJsonMatchIndex];
    if (match) {
      editor.setSelection(match.range);
      editor.revealRangeInCenter(match.range);
    }
  }, [tenantJsonMatches, tenantJsonMatchIndex]);

  const navigateTenantJsonMatch = (direction) => {
    if (!tenantJsonMatches.length) return;
    const nextIndex = (tenantJsonMatchIndex + direction + tenantJsonMatches.length) % tenantJsonMatches.length;
    setTenantJsonMatchIndex(nextIndex);
    const editor = tenantJsonEditorRef.current;
    editor?.setSelection(tenantJsonMatches[nextIndex].range);
    editor?.revealRangeInCenter(tenantJsonMatches[nextIndex].range);
  };

  const handleTenantJsonChange = (value) => {
    const draft = value ?? "";
    setTenantJsonDraft(draft);
    try {
      const parsed = JSON.parse(draft);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setTenantJsonError("Tenant JSON must be an object.");
        return;
      }
      lastAppliedTenantJsonRef.current = parsed;
      setTenantDetails(parsed);
      setTenantJsonError(null);
    } catch {
      setTenantJsonError("Fix the JSON syntax before saving.");
    }
  };

  const runTenantJsonAction = (actionId) => {
    tenantJsonEditorRef.current?.getAction(actionId)?.run();
  };
  const [enrichmentViewMode, setEnrichmentViewMode] = useState("form");
  const [enrichmentJsonDraft, setEnrichmentJsonDraft] = useState(() => JSON.stringify(importSettings, null, 2) ?? "");
  const [enrichmentJsonError, setEnrichmentJsonError] = useState(null);
  const lastAppliedEnrichmentJsonRef = React.useRef(importSettings);

  useEffect(() => {
    if (importSettings === lastAppliedEnrichmentJsonRef.current) return;
    lastAppliedEnrichmentJsonRef.current = importSettings;
    setEnrichmentJsonDraft(JSON.stringify(importSettings, null, 2) ?? "");
    setEnrichmentJsonError(null);
  }, [importSettings]);

  const handleEnrichmentJsonChange = (event) => {
    const draft = event.target.value;
    setEnrichmentJsonDraft(draft);
    setIsImportSettingsDirty(true);
    try {
      const parsed = JSON.parse(draft);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setEnrichmentJsonError("Enrichment settings must be a JSON object.");
        return;
      }
      if (parsed.rules !== undefined && (!Array.isArray(parsed.rules) || parsed.rules.some((rule) => !rule || typeof rule !== "object" || Array.isArray(rule)))) {
        setEnrichmentJsonError("Rules must be an array of rule objects.");
        return;
      }
      const settings = normalizeImportSettings(parsed);
      lastAppliedEnrichmentJsonRef.current = settings;
      setImportSettings(settings);
      setEnrichmentJsonError(null);
    } catch {
      setEnrichmentJsonError("Fix the JSON syntax before saving or switching to Form.");
    }
  };
  const [isConfigurationMapCompact, setIsConfigurationMapCompact] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customCachePrefix, setCustomCachePrefix] = useState("");
  const [clearingCacheType, setClearingCacheType] = useState(null);
  const [cacheMessage, setCacheMessage] = useState(null);
  const [cacheCounts, setCacheCounts] = useState(null);
  const [loadingCacheCounts, setLoadingCacheCounts] = useState(false);
  const [greetingKey, setGreetingKey] = useState("");
  const [productKey, setProductKey] = useState("");
  const [productCacheTarget, setProductCacheTarget] = useState("product");
  const [greetingEntries, setGreetingEntries] = useState([]);
  const [greetingEntriesOpen, setGreetingEntriesOpen] = useState(false);
  const [greetingEntriesSource, setGreetingEntriesSource] = useState("greeting");
  const [loadingGreetingEntries, setLoadingGreetingEntries] = useState(false);
  const [loadingCustomEntries, setLoadingCustomEntries] = useState(false);
  const [greetingEntriesFilter, setGreetingEntriesFilter] = useState("");
  const [selectedGreetingEntry, setSelectedGreetingEntry] = useState(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState(null);
  const [clearCacheConfirm, setClearCacheConfirm] = useState(null);
  const [jobsList, setJobsList] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [productDiscoveryCount, setProductDiscoveryCount] = useState(0);
  const [metafieldDefinitions, setMetafieldDefinitions] = useState({ product: [], variant: [] });
  const [loadingMetafields, setLoadingMetafields] = useState(false);
  const [metafieldsError, setMetafieldsError] = useState(null);
  const [metafieldsTab, setMetafieldsTab] = useState("definitions");
  const [productMetafieldId, setProductMetafieldId] = useState("");
  const [productMetafieldDefinitions, setProductMetafieldDefinitions] = useState({ product: [], variant: [] });
  const [loadingProductMetafields, setLoadingProductMetafields] = useState(false);
  const [productMetafieldsError, setProductMetafieldsError] = useState(null);
  const [productMetafieldPreview, setProductMetafieldPreview] = useState(null);
  const [productMetafieldPreviewOpen, setProductMetafieldPreviewOpen] = useState(false);
  const [loadingProductMetafieldPreview, setLoadingProductMetafieldPreview] = useState(false);
  const [productMetafieldPreviewError, setProductMetafieldPreviewError] = useState(null);
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteJobConfirm, setDeleteJobConfirm] = useState(null);
  const [jobForm, setJobForm] = useState({
    jobType: "product-update",
    cron: "0 */1 * * *",
    cronInterval: "1",
    cronUnit: "hours",
    indexName: "",
    webScrapeIndexName: "",
    forceSkipScrape: false,
  });
  const [savingJob, setSavingJob] = useState(false);
  const [triggeringJobId, setTriggeringJobId] = useState(null);
  const [jobMessage, setJobMessage] = useState(null);
  const [jobHistoryOpen, setJobHistoryOpen] = useState(false);
  const [jobHistoryData, setJobHistoryData] = useState({ runs: [], totalCount: 0, jobId: "" });
  const [jobHistoryLoading, setJobHistoryLoading] = useState(false);
  const [jobHistoryPage, setJobHistoryPage] = useState(1);
  const [greetingSplitPercent, setGreetingSplitPercent] = useState(40);
  const greetingDragging = React.useRef(false);
  const greetingContainerRef = React.useRef(null);

  // ── Integration / Reviews state ──────────────────────────────────────────
  const hasFullAdmin = isFullAdmin(getUserEmail());
  const REVIEW_PROVIDERS = ["BazaarVoice", "JudgeMe", "Okendo", "Yotpo"];
  const [reviewProvider, setReviewProvider] = useState("");
  const [reviewKeyName, setReviewKeyName] = useState("review-key");
  const [reviewApiKey, setReviewApiKey] = useState("");
  const [reviewRefreshFrequency, setReviewRefreshFrequency] = useState(12);
  const [reviewMaxPerProduct, setReviewMaxPerProduct] = useState(30);
  const [savingReviewKeys, setSavingReviewKeys] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState(null); // { type: "success"|"error", message }

  // ── Asset Management state ───────────────────────────────────────────────
  const [assetsList, setAssetsList] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsTotalCount, setAssetsTotalCount] = useState(0);
  const [assetsPage, setAssetsPage] = useState(0);
  const ASSETS_PAGE_SIZE = 20;
  const [assetsFeedback, setAssetsFeedback] = useState(null); // { type: "success"|"error", message }
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [uploadPopoverAnchor, setUploadPopoverAnchor] = useState(null);
  const [deletingAsset, setDeletingAsset] = useState(null); // fileName being deleted
  const [replaceTarget, setReplaceTarget] = useState(null); // { name } of asset being replaced
  const [replacingAsset, setReplacingAsset] = useState(false);
  const [deleteAssetConfirm, setDeleteAssetConfirm] = useState(null); // asset to confirm delete
  const [assetsViewMode, setAssetsViewMode] = useState("grid"); // "list" | "grid"
  const [previewAsset, setPreviewAsset] = useState(null);
  const [previewContent, setPreviewContent] = useState(null); // text content
  const [previewWordContent, setPreviewWordContent] = useState(null);
  const [previewWordError, setPreviewWordError] = useState("");
  const previewRequestIdRef = React.useRef(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCsvSearch, setPreviewCsvSearch] = useState("");
  const [copiedAssetName, setCopiedAssetName] = useState(null); // name of asset whose URL was just copied
  const [assetPickerTarget, setAssetPickerTarget] = useState(null); // { path, accept? } — field to populate
  const [assetsVisibilityFilter, setAssetsVisibilityFilter] = useState("all"); // "all" | "public" | "private"
  const [uploadVisibility, setUploadVisibility] = useState("public"); // "public" | "private"
  const [togglingVisibility, setTogglingVisibility] = useState(null); // fileName being toggled
  const uploadInputRef = React.useRef(null);
  const replaceInputRef = React.useRef(null);

  const handleSplitMouseDown = (e) => {
    e.preventDefault();
    greetingDragging.current = true;
    const onMouseMove = (ev) => {
      if (!greetingDragging.current || !greetingContainerRef.current) return;
      const rect = greetingContainerRef.current.getBoundingClientRect();
      const percent = ((ev.clientX - rect.left) / rect.width) * 100;
      setGreetingSplitPercent(Math.max(15, Math.min(85, percent)));
    };
    const onMouseUp = () => {
      greetingDragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const fetchCacheCounts = useCallback(async () => {
    if (!tenantDetails?.id) return;
    setLoadingCacheCounts(true);
    try {
      const response = await axiosInstance.get(`/cache/count/${tenantDetails.id}`, {
        headers: { Tenantid: tenantDetails.id },
      });
      setCacheCounts(response.data);
    } catch (error) {
      console.error("Error fetching cache counts:", error);
      setCacheCounts(null);
    } finally {
      setLoadingCacheCounts(false);
    }
  }, [tenantDetails?.id]);

  const fetchJobs = useCallback(async () => {
    if (!tenantDetails?.id) return;
    setLoadingJobs(true);
    try {
      const response = await axiosInstanceImporter.get(`/jobs/tenant/${tenantDetails.id}`);
      const data = response.data;
      if (Array.isArray(data)) {
        setJobsList(data);
      } else if (data?.jobs && Array.isArray(data.jobs)) {
        setJobsList(data.jobs);
      } else if (data?.items && Array.isArray(data.items)) {
        setJobsList(data.items);
      } else {
        setJobsList([]);
        setJobMessage({ type: "error", text: "Unexpected response format from jobs API." });
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobMessage({ type: "error", text: "Failed to load jobs." });
    } finally {
      setLoadingJobs(false);
    }
  }, [tenantDetails?.id]);

  const fetchMetafieldDefinitions = useCallback(async () => {
    if (!tenantDetails?.id) return;
    setLoadingMetafields(true);
    setMetafieldsError(null);
    try {
      const response = await axiosInstanceImporter.get("/Products/metafield-definitions", {
        params: { tenantId: tenantDetails.id },
      });
      setMetafieldDefinitions({
        product: mergeMetafieldGroups(response.data?.productMetafieldDefinitions, response.data?.productMetafields),
        variant: mergeMetafieldGroups(response.data?.variantMetafieldDefinitions, getExampleVariantMetafields(response.data)),
      });
    } catch (error) {
      console.error("Error fetching metafield definitions:", error);
      setMetafieldDefinitions({ product: [], variant: [] });
      setMetafieldsError("Unable to load metafields from Importer.");
    } finally {
      setLoadingMetafields(false);
    }
  }, [tenantDetails?.id]);

  const fetchProductMetafieldDefinitions = useCallback(async () => {
    const productId = productMetafieldId.trim();
    if (!tenantDetails?.id || !productId) return;
    setLoadingProductMetafields(true);
    setProductMetafieldsError(null);
    try {
      const response = await axiosInstanceImporter.get(`/Products/${encodeURIComponent(productId)}/metafields`, {
        headers: { TenantId: tenantDetails.id },
      });
      setProductMetafieldDefinitions({
        product: mergeMetafieldGroups(response.data?.productMetafieldDefinitions, response.data?.productMetafields),
        variant: mergeMetafieldGroups(response.data?.variantMetafieldDefinitions, getExampleVariantMetafields(response.data)),
      });
    } catch (error) {
      console.error("Error fetching product metafields:", error);
      setProductMetafieldDefinitions({ product: [], variant: [] });
      setProductMetafieldsError("Unable to load metafields for this product from Importer.");
    } finally {
      setLoadingProductMetafields(false);
    }
  }, [productMetafieldId, tenantDetails?.id]);

  const previewProductMetafields = useCallback(async () => {
    const productId = productMetafieldId.trim();
    if (!tenantDetails?.id || !productId) return;

    setLoadingProductMetafieldPreview(true);
    setProductMetafieldPreviewError(null);
    setProductMetafieldPreview(null);
    try {
      const response = await axiosInstanceImporter.get(`/Products/${encodeURIComponent(productId)}/preview`, {
        headers: { TenantId: tenantDetails.id },
      });
      setProductMetafieldPreview(response.data);
      setProductMetafieldPreviewOpen(true);
    } catch (error) {
      console.error("Error previewing product metafields:", error);
      setProductMetafieldPreviewError("Unable to preview metafields for this product from Importer.");
    } finally {
      setLoadingProductMetafieldPreview(false);
    }
  }, [productMetafieldId, tenantDetails?.id]);

  useEffect(() => {
    if (activeSection === "caches" && tenantDetails?.id) {
      setCacheMessage(null);
      fetchCacheCounts();
    }
    if (activeSection === "jobs") fetchJobs();
    if (activeSection !== "jobs") setJobMessage(null);
    if (activeSection === "metafields" && metafieldsTab === "definitions") fetchMetafieldDefinitions();
  }, [activeSection, tenantDetails?.id, fetchCacheCounts, fetchJobs, fetchMetafieldDefinitions, metafieldsTab]);

  // Pre-fill review integration fields from tenantDetails
  useEffect(() => {
    const integrationData = tenantDetails?.reviewApiKeys?.integrationData;
    const entry = Array.isArray(integrationData) ? integrationData[0] : null;
    if (entry?.provider && entry?.integration) {
      const { keys, reviewRefreshFrequencyHours, maxReviewsPerProduct } = entry.integration;
      setReviewProvider(entry.provider);
      setReviewKeyName("review-key");
      setReviewApiKey(keys?.["review-key"] ?? "");
      setReviewRefreshFrequency(reviewRefreshFrequencyHours ?? 12);
      setReviewMaxPerProduct(maxReviewsPerProduct ?? 30);
    } else {
      setReviewProvider("");
      setReviewKeyName("review-key");
      setReviewApiKey("");
      setReviewRefreshFrequency(12);
      setReviewMaxPerProduct(30);
    }
    setReviewFeedback(null);
  }, [tenantDetails?.id]);

  const handleLoadGreetingEntries = async () => {
    if (!tenantDetails?.id) return;
    setLoadingGreetingEntries(true);
    try {
      const response = await axiosInstance.get(`/cache/load-cache-entry/${tenantDetails.id}/GRT-URL-CHAT`, {
        headers: { Tenantid: tenantDetails.id },
      });
      setGreetingEntries(response.data || []);
      setGreetingEntriesFilter("");
      setSelectedGreetingEntry((response.data || []).length > 0 ? 0 : null);
      setGreetingEntriesSource("greeting");
      setGreetingEntriesOpen(true);
    } catch (error) {
      console.error("Error loading greeting cache entries:", error);
      setCacheMessage({ type: "error", text: "Failed to load greeting cache entries." });
    } finally {
      setLoadingGreetingEntries(false);
    }
  };

  const handleDeleteCacheKey = async (key) => {
    try {
      await axiosInstance.delete("/cache/clear-by-key", { data: key, headers: { "Content-Type": "application/json" } });
      setGreetingEntries((prev) => prev.filter((e) => (e.key ?? e.Key) !== key));
      setSelectedGreetingEntry(null);
      setDeleteConfirmKey(null);
      fetchCacheCounts();
    } catch (error) {
      console.error("Error deleting cache key:", error);
      setCacheMessage({ type: "error", text: `Failed to delete key: ${key}` });
      setDeleteConfirmKey(null);
    }
  };

  const handleLoadCustomEntries = async () => {
    if (!tenantDetails?.id) return;
    setLoadingCustomEntries(true);
    try {
      const prefix = customCachePrefix.trim() || "0";
      const url = `/cache/load-cache-entry/${tenantDetails.id}/${encodeURIComponent(prefix)}`;
      const response = await axiosInstance.get(url, {
        headers: { Tenantid: tenantDetails.id },
      });
      setGreetingEntries(response.data || []);
      setGreetingEntriesFilter("");
      setSelectedGreetingEntry((response.data || []).length > 0 ? 0 : null);
      setGreetingEntriesSource("custom");
      setGreetingEntriesOpen(true);
    } catch (error) {
      console.error("Error loading custom cache entries:", error);
      setCacheMessage({ type: "error", text: "Failed to load custom cache entries." });
    } finally {
      setLoadingCustomEntries(false);
    }
  };

  const handleSaveJob = async () => {
    if (!tenantDetails?.id) return;
    setSavingJob(true);
    try {
      if (editingJob) {
        await axiosInstanceImporter.put("/jobs", {
          jobId: editingJob.id,
          jobType: jobForm.jobType,
          tenantId: tenantDetails.id,
          indexName: jobForm.indexName,
          webScrapeIndexName: jobForm.webScrapeIndexName,
          cron: jobForm.cron,
          forceSkipScrape: jobForm.forceSkipScrape,
        });
        setJobMessage({ type: "success", text: "Job updated successfully." });
      } else {
        await axiosInstanceImporter.post("/jobs", {
          jobType: jobForm.jobType,
          tenantId: tenantDetails.id,
          indexName: jobForm.indexName,
          webScrapeIndexName: jobForm.webScrapeIndexName,
          cron: jobForm.cron,
          forceSkipScrape: jobForm.forceSkipScrape,
        });
        setJobMessage({ type: "success", text: "Job created successfully." });
      }
      setJobFormOpen(false);
      setEditingJob(null);
      fetchJobs();
    } catch (error) {
      console.error("Error saving job:", error);
      setJobMessage({ type: "error", text: `Failed to ${editingJob ? "update" : "create"} job.` });
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await axiosInstanceImporter.delete(`/jobs/${encodeURIComponent(jobId)}`);
      setJobsList((prev) => prev.filter((j) => j.id !== jobId));
      setDeleteJobConfirm(null);
      setJobMessage({ type: "success", text: "Job deleted successfully." });
    } catch (error) {
      console.error("Error deleting job:", error);
      setJobMessage({ type: "error", text: "Failed to delete job." });
      setDeleteJobConfirm(null);
    }
  };

  const handleTriggerJob = async (jobId) => {
    setTriggeringJobId(jobId);
    try {
      await axiosInstanceImporter.post(`/jobs/${encodeURIComponent(jobId)}/trigger`);
      setJobMessage({ type: "success", text: `Job '${jobId}' triggered.` });
    } catch (error) {
      console.error("Error triggering job:", error);
      setJobMessage({ type: "error", text: `Failed to trigger job '${jobId}'.` });
    } finally {
      setTriggeringJobId(null);
    }
  };

  const handleJobHistory = async (jobId, page = 1) => {
    setJobHistoryLoading(true);
    setJobHistoryOpen(true);
    setJobHistoryPage(page);
    try {
      const response = await axiosInstanceImporter.get(`/jobs/${encodeURIComponent(jobId)}/history`, { params: { page, pageSize: 20 } });
      setJobHistoryData({ runs: response.data.runs || [], totalCount: response.data.totalCount || 0, jobId });
    } catch (error) {
      console.error("Error fetching job history:", error);
      setJobHistoryData({ runs: [], totalCount: 0, jobId });
    } finally {
      setJobHistoryLoading(false);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    const cron = job.cron || "";
    const parts = cron.split(/\s+/);
    let cronInterval = "1";
    let cronUnit = "custom";
    // Parse */N patterns: minutes, hours, days
    if (parts.length === 5) {
      if (parts[0].startsWith("*/") && parts[1] === "*" && parts[2] === "*") {
        cronUnit = "minutes";
        cronInterval = parts[0].replace("*/", "") || "1";
      } else if (parts[0] === "0" && parts[1].startsWith("*/") && parts[2] === "*") {
        cronUnit = "hours";
        cronInterval = parts[1].replace("*/", "") || "1";
      } else if (parts[0] === "0" && parts[1] === "0" && parts[2].startsWith("*/")) {
        cronUnit = "days";
        cronInterval = parts[2].replace("*/", "") || "1";
      }
    }
    setJobForm({
      jobType: job.id.startsWith("scrape-products") ? "scrape-products" : "product-update",
      cron,
      cronInterval,
      cronUnit,
      indexName: job.indexName || job.elasticIndexName || job.tenantIndexName || tenantDetails?.tenantAiProfile?.elasticIndexName || "",
      webScrapeIndexName: job.webScrapeIndexName || job.webScrapeIndex || tenantDetails?.webScrapeIndexName || "",
      forceSkipScrape: Boolean(job.forceSkipScrape),
    });
    setJobFormOpen(true);
  };

  const handleNewJob = () => {
    setEditingJob(null);
    setJobForm({
      jobType: "product-update",
      cron: "0 */1 * * *",
      cronInterval: "1",
      cronUnit: "hours",
      indexName: tenantDetails?.tenantAiProfile?.elasticIndexName || "",
      webScrapeIndexName: tenantDetails?.webScrapeIndexName || "",
      forceSkipScrape: false,
    });
    setJobFormOpen(true);
  };

  const updateValue = (path, nextValue) => {
    setTenantDetails((previous) => cloneWithUpdatedValue(previous, path, nextValue));
  };

  const updateDataFeed = (path, nextValue) => {
    setTenantDetails((previous) => {
      let updatedTenant = cloneWithUpdatedValue(previous, path, nextValue);

      if (nextValue === "Shopify") {
        updatedTenant = cloneWithUpdatedValue(
          updatedTenant,
          "dataFeedConnector.configurations.dataType",
          "JSON"
        );
      }

      return updatedTenant;
    });
  };

  const updateGraphQLToggle = (path, nextValue) => {
    setTenantDetails((previous) => {
      let updatedTenant = cloneWithUpdatedValue(previous, path, nextValue);

      if (toBoolean(nextValue)) {
        const otherPath =
          path === "dataFeedConnector.configurations.useGraphQLBulkQuery"
            ? "dataFeedConnector.configurations.useGraphQL"
            : "dataFeedConnector.configurations.useGraphQLBulkQuery";
        updatedTenant = cloneWithUpdatedValue(updatedTenant, otherPath, "false");
      }

      return updatedTenant;
    });
  };

  const updateProductSalesLookbackDays = (path, nextValue) => {
    if (nextValue !== "" && !/^\d+$/.test(nextValue)) return;

    updateValue(path, nextValue !== "" && Number(nextValue) > 60 ? "60" : nextValue);
  };

  const normaliseProductSalesLookbackDays = (path, value) => {
    const parsedValue = Number.parseInt(value, 10);
    const boundedValue = Number.isFinite(parsedValue)
      ? Math.min(60, Math.max(30, parsedValue))
      : 30;

    updateValue(path, String(boundedValue));
  };

  const featureConfigurations = tenantDetails?.tenantAiProfile?.featureConfigurations || {};
  const widgetSettings = tenantDetails?.tenantAiProfile?.widgetConfig?.settingsConfig || {};
  const themeConfig = tenantDetails?.tenantAiProfile?.widgetConfig?.themeConfig || {};
  const pdpThemeConfig = tenantDetails?.tenantAiProfile?.widgetConfig?.pdpThemeConfig || {};
  const dataFeedConfigurations = tenantDetails?.dataFeedConnector?.configurations || {};
  const shopifyAppInstallStatus = dataFeedConfigurations.appInstallStatus
    ?? tenantDetails?.dataFeedConnector?.shopifyApiStatus
    ?? "";
  const defaultShopifyRedirectUrl = `${window.location.origin}/shopify/integration`;
  const shopifyRedirectUrl = dataFeedConfigurations.shopifyRedirectUrl ?? defaultShopifyRedirectUrl;
  const shopifyAppUrl = dataFeedConfigurations.shopifyAppUrl
    ?? `${defaultShopifyRedirectUrl}?tenant-id=${encodeURIComponent(tenantDetails?.id ?? "")}`;
  const isShopifyDataFeed = dataFeedConfigurations.dataFeed === "Shopify";
  const isGraphQLBulkQueryEnabled = toBoolean(dataFeedConfigurations.useGraphQLBulkQuery ?? false);
  const isGraphQLEnabled = toBoolean(dataFeedConfigurations.useGraphQL ?? false);
  const productMetafieldValues = parseMetafieldValues(featureConfigurations.ProductMetafields);
  const sizeInformationMetafieldValues = parseMetafieldValues(featureConfigurations.SizeInformationMetafields);
  const requiredProductMetafieldValues = [...new Set([
    ...sizeInformationMetafieldValues,
    ...parseMetafieldValues(featureConfigurations.SizeChartMetafield),
  ])];

  const updateMetafieldSelection = (key, nextValue) => {
    setTenantDetails((previous) => {
      const updated = cloneWithUpdatedValue(previous, `tenantAiProfile.featureConfigurations.${key}`, nextValue);
      const configurations = updated.tenantAiProfile.featureConfigurations;
      const products = [...new Set([
        ...parseMetafieldValues(configurations.ProductMetafields),
        ...parseMetafieldValues(configurations.SizeInformationMetafields),
        ...parseMetafieldValues(configurations.SizeChartMetafield),
      ])];
      return cloneWithUpdatedValue(updated, "tenantAiProfile.featureConfigurations.ProductMetafields", products.join(","));
    });
  };

  useEffect(() => {
    if (activeSection !== "metafields") return;
    setTenantDetails((previous) => {
      const configurations = previous?.tenantAiProfile?.featureConfigurations || {};
      const products = parseMetafieldValues(configurations.ProductMetafields);
      const merged = [...new Set([
        ...products,
        ...parseMetafieldValues(configurations.SizeInformationMetafields),
        ...parseMetafieldValues(configurations.SizeChartMetafield),
      ])];
      if (merged.length === products.length) return previous;
      return cloneWithUpdatedValue(previous, "tenantAiProfile.featureConfigurations.ProductMetafields", merged.join(","));
    });
  }, [activeSection, featureConfigurations.ProductMetafields, featureConfigurations.SizeInformationMetafields, featureConfigurations.SizeChartMetafield, setTenantDetails]);

  const variantMetafieldValues = parseMetafieldValues(featureConfigurations.VariantMetafields);
  const sizeChartMetafieldValues = parseMetafieldValues(featureConfigurations.SizeChartMetafield);

  useEffect(() => {
    if (!isShopifyDataFeed || dataFeedConfigurations.dataType === "JSON") return;

    setTenantDetails((previous) => {
      const configurations = previous?.dataFeedConnector?.configurations;
      if (configurations?.dataFeed !== "Shopify" || configurations?.dataType === "JSON") {
        return previous;
      }

      return cloneWithUpdatedValue(
        previous,
        "dataFeedConnector.configurations.dataType",
        "JSON"
      );
    });
  }, [dataFeedConfigurations.dataType, isShopifyDataFeed, setTenantDetails]);

  const booleanFeatureKeys = Object.keys(featureConfigurations).filter(
    (key) => !importerMerchandisingConfigKeys.includes(key) && isBooleanLike(featureConfigurations[key])
  );
  const textFeatureKeys = Object.keys(featureConfigurations).filter(
    (key) =>
      key !== "SemanticSearchThreshold" &&
      !importerMerchandisingConfigKeys.includes(key) &&
      !isBooleanLike(featureConfigurations[key])
  );
  const semanticSearchThreshold = featureConfigurations.SemanticSearchThreshold ?? 0;
  const booleanWidgetKeys = Object.keys(widgetSettings).filter((key) => isBooleanLike(widgetSettings[key]));
  const advancedWidgetKeys = Object.keys(widgetSettings).filter((key) => !isBooleanLike(widgetSettings[key]));

  const sectionGroups = useMemo(
    () => [
      {
        groupId: "general",
        groupTitle: "General",
        groupIcon: DashboardOutlinedIcon,
        items: [
          {
            id: "overview",
            title: "Overview",
            icon: DashboardOutlinedIcon,
            count: 6 + countEntries(tenantDetails?.industries),
          },
          {
            id: "assets",
            title: "Assets",
            icon: FolderOutlinedIcon,
            count: assetsTotalCount,
            adminOnly: tenantDetails?.id === PREEZIE_ROOT_TENANT_ID,
          },
          {
            id: "order-tracking",
            title: "Order Tracking",
            icon: LocalShippingOutlinedIcon,
            count: 1,
          },
        ],
      },
      {
        groupId: "data",
        groupTitle: "Data",
        groupIcon: StorageOutlinedIcon,
        items: [
          {
            id: "data-index",
            title: "Feed & Indexing",
            icon: StorageOutlinedIcon,
            count: countEntries(tenantDetails?.dataFeedConnector?.configurations) + 6,
          },
          {
            id: "enrichment",
            title: "Enrichment",
            icon: TuneOutlinedIcon,
            count: (importSettings?.rules?.length || 0) + 3,
          },
          {
            id: "metafields",
            title: "Metafields",
            icon: DataObjectOutlinedIcon,
            count: productMetafieldValues.length + variantMetafieldValues.length + sizeChartMetafieldValues.length + sizeInformationMetafieldValues.length,
          },
          ...(SHOW_SHOPIFY_TAXONOMY ? [{
            id: "taxonomy",
            title: "Taxonomy",
            icon: PublicOutlinedIcon,
            count: Array.isArray(tenantDetails?.shopifyTaxonomyIds)
              ? tenantDetails.shopifyTaxonomyIds.length
              : Array.isArray(tenantDetails?.ShopifyTaxonomyIds)
                ? tenantDetails.ShopifyTaxonomyIds.length
                : 0,
          }] : []),
          {
            id: "jobs",
            title: "Jobs",
            icon: ScheduleOutlinedIcon,
            count: 0,
          },
          {
            id: "cache-v2",
            title: "Cache V2",
            icon: CachedOutlinedIcon,
            count: 5,
          },
        ],
      },
      {
        groupId: "ai-assistant",
        groupTitle: "AI Assistant",
        groupIcon: PsychologyOutlinedIcon,
        items: [
          {
            id: "ai-profile",
            title: "Profile & Prompts",
            icon: PsychologyOutlinedIcon,
            count: countEntries(tenantDetails?.tenantAiProfile?.variables),
          },
          {
            id: "feature-flags",
            title: "Features",
            icon: ToggleOnOutlinedIcon,
            count: countEntries(tenantDetails?.tenantAiProfile?.featureConfigurations),
          },
          {
            id: "merchandising",
            title: "Product Discovery",
            icon: StorefrontOutlinedIcon,
            count: productDiscoveryCount,
          },
          {
            id: "integration",
            title: "Integration",
            icon: ExtensionOutlinedIcon,
            count: 1,
            fullAdminOnly: true,
          },
        ],
      },
      {
        groupId: "widget",
        groupTitle: "Widget",
        groupIcon: WidgetsOutlinedIcon,
        items: [
          {
            id: "widget-settings",
            title: "Behaviour & Settings",
            icon: WidgetsOutlinedIcon,
            count: countEntries(tenantDetails?.tenantAiProfile?.widgetConfig?.settingsConfig),
          },
          {
            id: "widget-theme",
            title: "Appearance",
            icon: PaletteOutlinedIcon,
            count: countEntries(tenantDetails?.tenantAiProfile?.widgetConfig?.themeConfig),
          },
          {
            id: "pdp-theme",
            title: "PDP",
            icon: StoreOutlinedIcon,
            count: countEntries(tenantDetails?.tenantAiProfile?.widgetConfig?.pdpThemeConfig),
          },
        ],
      },
      {
        groupId: "advanced",
        groupTitle: "Advanced",
        groupIcon: CodeIcon,
        items: [
          {
            id: "advanced",
            title: "Metadata",
            icon: CodeIcon,
            count: 5,
          },
          {
            id: "tenant-json",
            title: "Tenant JSON",
            icon: DataObjectOutlinedIcon,
            count: countEntries(tenantDetails),
            showCount: false,
          },
        ],
      },
    ],
    [tenantDetails, assetsTotalCount, productMetafieldValues.length, variantMetafieldValues.length, sizeChartMetafieldValues.length, sizeInformationMetafieldValues.length, productDiscoveryCount]
  );

  // Flatten groups into a flat list for compatibility with existing logic
  const sections = useMemo(
    () => sectionGroups.flatMap((group) => group.items),
    [sectionGroups]
  );

  const visibleSections = useMemo(
    () => sections.filter((s) => {
      if (s.fullAdminOnly) return hasFullAdmin;
      if (s.adminOnly) return isAdmin;
      return true;
    }),
    [sections, isAdmin, hasFullAdmin]
  );

  useEffect(() => {
    if (!visibleSections.some((section) => section.id === activeSection)) {
      setActiveSection(visibleSections[0]?.id || "overview");
    }
  }, [activeSection, visibleSections, setActiveSection]);

  const visibleSectionGroups = useMemo(
    () => sectionGroups.map((group) => ({
      ...group,
      items: group.items.filter((s) => {
        if (s.fullAdminOnly) return hasFullAdmin;
        if (s.adminOnly) return isAdmin;
        return true;
      }),
    })).filter((group) => group.items.length > 0),
    [sectionGroups, isAdmin, hasFullAdmin]
  );

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSectionClick = (sectionId, groupId) => {
    setActiveSection(sectionId);
    if (!expandedGroups.includes(groupId)) {
      setExpandedGroups((prev) => [...prev, groupId]);
    }
  };

  const collectObjectFields = (obj, excludeKeys = []) =>
    Object.entries(obj || {})
      .filter(([key]) => !excludeKeys.includes(key))
      .map(([key, value]) => ({ label: prettifyKey(key), value }));

  const sectionFieldsMap = useMemo(() => {
    const overviewFields = [
      { label: "Tenant Name", value: tenantDetails?.name },
      { label: "Website URL", value: tenantDetails?.websiteUrl },
      { label: "Shopify Domain", value: tenantDetails?.shopifyDomain },
      { label: "Local Time Zone", value: tenantDetails?.localTimeZone },
      { label: "Version", value: tenantDetails?.version },
      { label: "Active", value: tenantDetails?.active },
      { label: "Demo Account", value: tenantDetails?.demoAccount },
      { label: "Industries", value: tenantDetails?.industries },
    ];

    const dataIndexFields = [
      { label: "Elastic Index Name", value: tenantDetails?.tenantAiProfile?.elasticIndexName },
      { label: "Policy Index Name", value: tenantDetails?.tenantAiProfile?.policyIndexName },
      { label: "Web Scrape Index Name", value: tenantDetails?.webScrapeIndexName },
      { label: "Display Size Override", value: tenantDetails?.tenantAiProfile?.displaySizeOverride },
      { label: "Knowledge Base Max Docs", value: tenantDetails?.tenantAiProfile?.knowledgeBaseMaxDocs },
      { label: `Include in Nightly Update (${toBoolean(tenantDetails?.tenantAiProfile?.nightlyProductUpdate) ? "ON" : "OFF"})`, value: tenantDetails?.tenantAiProfile?.nightlyProductUpdate },
      { label: "Use GraphQL Bulk Query", value: tenantDetails?.dataFeedConnector?.configurations?.useGraphQLBulkQuery ?? "false" },
      { label: "Use GraphQL API", value: tenantDetails?.dataFeedConnector?.configurations?.useGraphQL ?? "false" },
      ...(isShopifyDataFeed ? [
        { label: "App install status", value: shopifyAppInstallStatus },
        { label: "Shopify App URL", value: shopifyAppUrl },
        { label: "Shopify Redirect URL", value: shopifyRedirectUrl },
        { label: "Manual secret setup Enter Client ID Enter Client Secret Save client and secret", value: "" },
      ] : []),
      { label: "Enable Product Sales Count", value: featureConfigurations.EnableProductSalesCount ?? "true" },
      { label: "Product Sales Lookback Days", value: featureConfigurations.ProductSalesLookbackDays ?? "30" },
      ...collectObjectFields(tenantDetails?.dataFeedConnector?.configurations),
    ];

    const advancedFields = [
      { label: "Tenant ID", value: tenantDetails?.id },
      { label: "AI Profile ID", value: tenantDetails?.tenantAiProfile?.id },
      { label: "Created At", value: tenantDetails?.createDate },
      { label: "Updated At", value: tenantDetails?.updateDate },
      { label: "Published At", value: tenantDetails?.publishedAt },
    ];

    const enrichmentFields = [
      { label: "Product ID Field Name", value: importSettings?.productIdFieldName },
      { label: "Title Field Name", value: importSettings?.titleFieldName },
      { label: "Has Parent Product", value: importSettings?.hasParentProduct },
      { label: "Use Preezie Generated ID", value: importSettings?.usePreezieGeneratedId },
      { label: "Use Aggregation Value As Document ID", value: importSettings?.useAggregationValueAsDocumentId },
      ...(importSettings?.rules || []).map((rule, i) => ({ label: `Rule ${i + 1} ${rule.type}`, value: rule.script })),
    ];

    return {
      overview: overviewFields,
      "data-index": dataIndexFields,
      enrichment: enrichmentFields,
      metafields: [
        { label: "Product Metafields", value: featureConfigurations.ProductMetafields },
        { label: "Variant Metafields", value: featureConfigurations.VariantMetafields },
        { label: "Size Chart Metafields", value: featureConfigurations.SizeChartMetafield },
        { label: "Sizing Information", value: featureConfigurations.SizeInformationMetafields },
      ],
      taxonomy: [
        { label: "Shopify Taxonomy IDs", value: tenantDetails?.shopifyTaxonomyIds ?? tenantDetails?.ShopifyTaxonomyIds },
      ],
      "ai-profile": collectObjectFields(tenantDetails?.tenantAiProfile?.variables),
      "feature-flags": collectObjectFields(featureConfigurations, importerMerchandisingConfigKeys),
      "widget-settings": collectObjectFields(widgetSettings),
      "widget-theme": collectObjectFields(themeConfig),
      "pdp-theme": collectObjectFields(pdpThemeConfig),
      advanced: advancedFields,
      "tenant-json": [{ label: "Tenant JSON Raw JSON fallback", value: tenantDetails }],
    };
  }, [tenantDetails, featureConfigurations, widgetSettings, themeConfig, pdpThemeConfig, importSettings, isShopifyDataFeed, shopifyAppInstallStatus, shopifyAppUrl, shopifyRedirectUrl]);

  const sectionsWithMatches = useMemo(() => {
    if (!searchQuery.trim()) return new Set(visibleSections.map((s) => s.id));
    const matched = new Set();
    for (const section of visibleSections) {
      const fields = sectionFieldsMap[section.id] || [];
      if (fields.some((f) => matchesSearch(f.label, f.value, searchQuery))) {
        matched.add(section.id);
      }
    }
    return matched;
  }, [searchQuery, visibleSections, sectionFieldsMap]);

  useEffect(() => {
    if (!searchQuery.trim()) return;
    if (sectionsWithMatches.has(activeSection)) return;
    if (sectionsWithMatches.size > 0) {
      const firstMatch = visibleSections.find((s) => sectionsWithMatches.has(s.id));
      if (firstMatch) {
        setActiveSection(firstMatch.id);
        const parentGroup = visibleSectionGroups.find((g) => g.items.some((i) => i.id === firstMatch.id));
        if (parentGroup && !expandedGroups.includes(parentGroup.groupId)) {
          setExpandedGroups((prev) => [...prev, parentGroup.groupId]);
        }
      }
    }
  }, [searchQuery, sectionsWithMatches, activeSection, visibleSections, visibleSectionGroups, expandedGroups]);

  const activeSectionMeta = visibleSections.find((section) => section.id === activeSection) || visibleSections[0];

  const handleClearCache = async (type) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId) return;

    const cacheTypeMap = { greetings: "Greeting", greetingTarget: "Greeting", products: "Product", custom: "Custom" };
    const payload = {
      TenantId: tenantId,
      Type: cacheTypeMap[type],
    };

    if (type === "greetings" && greetingKey.trim()) {
      payload.Key = greetingKey.trim();
    } else if (type === "greetingTarget" && productKey.trim()) {
      if (productCacheTarget === "product") {
        payload.ProductId = productKey.trim();
      } else {
        payload.ProductId = null;
        payload.ProductUrl = productKey.trim();
      }
    } else if (type === "custom") {
      if (!customCachePrefix.trim()) {
        setCacheMessage({ type: "error", text: "Please enter a cache key." });
        return;
      }
      payload.Key = customCachePrefix.trim();
    }

    setClearingCacheType(type);
    setCacheMessage(null);

    try {
      await axiosInstance.delete("/cache/clear", {
        data: payload,
        headers: { Tenantid: tenantId },
      });
      setCacheMessage({ type: "success", text: `Cache cleared successfully for ${cacheTypeMap[type]}${type === "custom" ? `: ${customCachePrefix.trim()}` : ""}` });
      fetchCacheCounts();
    } catch (error) {
      console.error("Error clearing cache:", error);
      setCacheMessage({ type: "error", text: error.response?.data?.message || "Failed to clear cache." });
    } finally {
      setClearingCacheType(null);
    }
  };

  // ── Asset Management handlers ────────────────────────────────────────────

  const fetchAssets = useCallback(async (pageNum = 0) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !canManageAssets) return;
    setAssetsLoading(true);
    setAssetsFeedback(null);
    try {
      const visParam = assetsVisibilityFilter !== "all" ? `&visibility=${assetsVisibilityFilter}` : "";
      const response = await axiosInstance.get(
        `/assets/${tenantId}?page=${pageNum + 1}&pageSize=${ASSETS_PAGE_SIZE}${visParam}`
      );
      const data = response.data;
      setAssetsList(data.items || []);
      setAssetsTotalCount(data.totalCount ?? 0);
      setAssetsPage(pageNum);
    } catch (error) {
      console.error("Error fetching assets:", error);
      setAssetsFeedback({ type: "error", message: "Failed to load assets." });
    } finally {
      setAssetsLoading(false);
    }
  }, [tenantDetails?.id, assetsVisibilityFilter, canManageAssets]);

  useEffect(() => {
    if (activeSection === "assets" && tenantDetails?.id) {
      fetchAssets(0);
    }
  }, [activeSection, tenantDetails?.id]);

  useEffect(() => {
    if (activeSection === "assets" && tenantDetails?.id) {
      fetchAssets(0);
    }
  }, [assetsVisibilityFilter]);

  // Fetch thumbnails for image assets whenever the list changes
  const handleAssetUpload = async (file) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !file) return;
    setUploadingAsset(true);
    setAssetsFeedback(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axiosInstance.post(`/assets/${tenantId}?visibility=${uploadVisibility}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAssetsFeedback({ type: "success", message: `"${file.name}" uploaded as ${uploadVisibility}.` });
      fetchAssets(assetsPage);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "Upload failed.";
      setAssetsFeedback({ type: "error", message: String(msg) });
    } finally {
      setUploadingAsset(false);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  const handleAssetReplace = async (file) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !file || !replaceTarget) return;
    setReplacingAsset(true);
    setAssetsFeedback(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axiosInstance.put(`/assets/${tenantId}/${encodeURIComponent(replaceTarget.name)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAssetsFeedback({ type: "success", message: `"${replaceTarget.name}" replaced successfully.` });
      fetchAssets(assetsPage);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "Replace failed.";
      setAssetsFeedback({ type: "error", message: String(msg) });
    } finally {
      setReplacingAsset(false);
      setReplaceTarget(null);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  };

  const handleAssetDelete = async (asset) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !asset) return;
    setDeletingAsset(asset.name);
    setAssetsFeedback(null);
    try {
      await axiosInstance.delete(`/assets/${tenantId}/${encodeURIComponent(asset.name)}`);
      setAssetsFeedback({ type: "success", message: `"${asset.name}" deleted.` });
      fetchAssets(assetsPage);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "Delete failed.";
      setAssetsFeedback({ type: "error", message: String(msg) });
    } finally {
      setDeletingAsset(null);
      setDeleteAssetConfirm(null);
    }
  };

  const handleToggleVisibility = async (asset) => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !asset) return;
    const newVisibility = asset.visibility?.toLowerCase() === "public" ? "private" : "public";
    setTogglingVisibility(asset.name);
    setAssetsFeedback(null);
    try {
      await axiosInstance.patch(
        `/assets/${tenantId}/${encodeURIComponent(asset.name)}/visibility`,
        { visibility: newVisibility }
      );
      setAssetsFeedback({ type: "success", message: `"${asset.name}" is now ${newVisibility}.` });
      fetchAssets(assetsPage);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "Failed to change visibility.";
      setAssetsFeedback({ type: "error", message: String(msg) });
    } finally {
      setTogglingVisibility(null);
    }
  };

  const formatAssetSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const FILE_EXT_COLORS = {
    png:  { bg: "#ede9fe", label: "#7c3aed", border: "#c4b5fd" },
    jpg:  { bg: "#fce7f3", label: "#be185d", border: "#f9a8d4" },
    jpeg: { bg: "#fce7f3", label: "#be185d", border: "#f9a8d4" },
    gif:  { bg: "#fef3c7", label: "#b45309", border: "#fcd34d" },
    webp: { bg: "#f0fdf4", label: "#15803d", border: "#86efac" },
    svg:  { bg: "#fff7ed", label: "#c2410c", border: "#fdba74" },
    pdf:  { bg: "#fee2e2", label: "#b91c1c", border: "#fca5a5" },
    csv:  { bg: "#dcfce7", label: "#166534", border: "#86efac" },
    xlsx: { bg: "#dcfce7", label: "#166534", border: "#86efac" },
    xls:  { bg: "#dcfce7", label: "#166534", border: "#86efac" },
    docx: { bg: "#dbeafe", label: "#1d4ed8", border: "#93c5fd" },
    doc:  { bg: "#dbeafe", label: "#1d4ed8", border: "#93c5fd" },
    json: { bg: "#fff7ed", label: "#ea580c", border: "#fdba74" },
    txt:  { bg: "#f3f4f6", label: "#374151", border: "#d1d5db" },
  };

  const getAssetIcon = (fileName = "", _contentType = "", size = 36) => {
    const ext = (fileName.includes(".") ? fileName.split(".").pop() : "").toLowerCase();
    const colors = FILE_EXT_COLORS[ext] || { bg: "#f3f4f6", label: "#6b7280", border: "#d1d5db" };
    const label = (ext ? ext.toUpperCase() : "FILE").slice(0, 4);
    // Fixed viewBox 40×52 — SVG scales to any size cleanly
    const w = size;
    const h = Math.round(size * 1.3);
    return (
      <svg
        width={w}
        height={h}
        viewBox="0 0 40 52"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ flexShrink: 0, display: "block" }}
      >
        {/* File body */}
        <path
          d="M4,0 H28 L40,12 V48 Q40,52 36,52 H4 Q0,52 0,48 V4 Q0,0 4,0 Z"
          fill={colors.bg}
          stroke={colors.border}
          strokeWidth="2"
        />
        {/* Fold triangle */}
        <path
          d="M28,0 L40,12 H30 Q28,12 28,10 Z"
          fill={colors.border}
        />
        {/* Extension label — large enough to read at any size */}
        <text
          x="20"
          y="40"
          textAnchor="middle"
          fontSize={label.length <= 3 ? "13" : "10"}
          fontWeight="800"
          fontFamily="system-ui, -apple-system, sans-serif"
          fill={colors.label}
          letterSpacing="0.3"
        >
          {label}
        </text>
      </svg>
    );
  };

  const TEXT_PREVIEW_TYPES = ["application/json", "text/plain", "text/csv"];
  const isImageType = (ct = "") => ct.startsWith("image/");
  const isPdfType = (ct = "") => ct === "application/pdf";
  const isTextType = (ct = "") => TEXT_PREVIEW_TYPES.includes(ct);
  const isDocxType = (fileName = "", ct = "") => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    return ct.toLowerCase() === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || extension === "docx";
  };
  const isLegacyDocType = (fileName = "", ct = "") => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    return ct.toLowerCase() === "application/msword" || extension === "doc";
  };
  const PREVIEW_SIZE_LIMIT = 3 * 1024 * 1024; // 3 MB

  const handleOpenAssetPreview = async (asset) => {
    const previewRequestId = ++previewRequestIdRef.current;
    if ((asset.size || 0) > PREVIEW_SIZE_LIMIT) {
      // File too large — open dialog in download-only mode
      setPreviewAsset(asset);
      setPreviewContent(null);
      setPreviewWordContent(null);
      setPreviewWordError("");
      return;
    }
    setPreviewAsset(asset);
    setPreviewContent(null);
    setPreviewWordContent(null);
    setPreviewWordError("");
    if (isLegacyDocType(asset.name, asset.contentType) && !getPublicOfficePreviewUrl(asset)) {
      setPreviewWordError("Private DOC preview is not available yet.");
    } else if (isDocxType(asset.name, asset.contentType)) {
      setPreviewLoading(true);
      try {
        const response = await axiosInstance.get(
          `/assets/${tenantDetails?.id}/${encodeURIComponent(asset.name)}/download`,
          { responseType: "arraybuffer" }
        );
        const content = await extractDocxPreview(response.data);
        if (previewRequestId === previewRequestIdRef.current) setPreviewWordContent(content);
      } catch (error) {
        if (previewRequestId === previewRequestIdRef.current) {
          setPreviewWordError(error?.message || "Unable to load this DOCX document for preview.");
        }
      } finally {
        if (previewRequestId === previewRequestIdRef.current) setPreviewLoading(false);
      }
    } else if (isTextType(asset.contentType)) {
      setPreviewLoading(true);
      try {
        const response = await axiosInstance.get(
          `/assets/${tenantDetails?.id}/${encodeURIComponent(asset.name)}/content`,
          { responseType: "text" }
        );
        if (previewRequestId === previewRequestIdRef.current) setPreviewContent(response.data);
      } catch {
        try {
          const r = await fetch(asset.url);
          const content = await r.text();
          if (previewRequestId === previewRequestIdRef.current) setPreviewContent(content);
        } catch {
          if (previewRequestId === previewRequestIdRef.current) setPreviewContent("Unable to load file content.");
        }
      } finally {
        if (previewRequestId === previewRequestIdRef.current) setPreviewLoading(false);
      }
    }
  };

  const handleCloseAssetPreview = () => {
    previewRequestIdRef.current += 1;
    setPreviewAsset(null);
    setPreviewContent(null);
    setPreviewWordContent(null);
    setPreviewWordError("");
    setPreviewLoading(false);
    setPreviewCsvSearch("");
  };

  const handleCopyAssetUrl = (asset) => {
    if (!asset.url) return;
    navigator.clipboard.writeText(asset.url).then(() => {
      setCopiedAssetName(asset.name);
      setTimeout(() => setCopiedAssetName(null), 2000);
    });
  };

  const handleSaveReviewKeys = async () => {
    const tenantId = tenantDetails?.id;
    if (!tenantId || !reviewProvider || !reviewApiKey.trim()) return;
    setSavingReviewKeys(true);
    setReviewFeedback(null);
    try {
      await axiosInstance.put(`/integration/${tenantId}/api-keys`, {
        integrationType: "Reviews",
        integrationData: [
          {
            provider: reviewProvider.toLowerCase(),
            integration: {
              keys: { [reviewKeyName]: reviewApiKey.trim() },
              reviewRefreshFrequencyHours: Number(reviewRefreshFrequency),
              maxReviewsPerProduct: Number(reviewMaxPerProduct),
            },
          },
        ],
      });
      setReviewFeedback({ type: "success", message: "Review integration saved successfully." });
    } catch (error) {
      console.error("Error saving review integration:", error);
      setReviewFeedback({
        type: "error",
        message: error.response?.data?.message || "Failed to save. Please try again.",
      });
    } finally {
      setSavingReviewKeys(false);
    }
  };

  const renderActiveSection = () => {
    if (!visibleSections.some((section) => section.id === activeSection)) return null;

    switch (activeSection) {
      case "overview":
        return (
          <SectionView title="Overview" description="Tenant identity, publishing state, and business metadata." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ConfigField label="Tenant ID" path="id" value={tenantDetails?.id || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigField label="Tenant Name" path="name" value={tenantDetails?.name || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigField label="Website URL" path="websiteUrl" value={tenantDetails?.websiteUrl || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigField label="Shopify Domain" path="shopifyDomain" value={tenantDetails?.shopifyDomain || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ConfigField label="Local Time Zone" path="localTimeZone" value={tenantDetails?.localTimeZone || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ConfigField label="Version" path="version" value={tenantDetails?.version ?? 0} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ConfigField label="Active" path="active" value={tenantDetails?.active} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ConfigField label="Demo Account" path="demoAccount" value={tenantDetails?.demoAccount} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ConfigField label="Industries" path="industries" value={tenantDetails?.industries || []} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={8}>
                <ConfigField
                  label="Show PDP widget on all product pages, regardless of stock status"
                  path="tenantAiProfile.widgetConfig.settingsConfig.shopifyAlwaysShowPDP"
                  value={toBoolean(tenantDetails?.tenantAiProfile?.widgetConfig?.settingsConfig?.shopifyAlwaysShowPDP)}
                  onValueChange={updateValue}
                  searchQuery={searchQuery}
                />
              </Grid>
            </Grid>
          </SectionView>
        );
      case "order-tracking":
        return (
          <SectionView
            title="Order Tracking"
            description="Choose the tenant's commerce provider and configure how shipment details are read."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
          >
            <OrderTrackingSettings tenantId={tenantDetails?.id} showHeading={false} />
          </SectionView>
        );
      case "ai-profile":
        return (
          <SectionView title="AI Profile" description="Prompting, brand context, and model-facing inputs." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <ExpandableFieldGrid objectValue={tenantDetails?.tenantAiProfile?.variables} basePath="tenantAiProfile.variables" searchQuery={searchQuery} onValueChange={updateValue} />
          </SectionView>
        );
      case "enrichment":
        return (
          <SectionView
            title="Enrichment Settings"
            description="Source mapping fields, enrichment rules, and transformation behavior."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
            action={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  value={enrichmentViewMode}
                  exclusive
                  onChange={(_event, value) => value && setEnrichmentViewMode(value)}
                  size="small"
                  aria-label="Enrichment settings view"
                  disabled={!importSettings}
                  sx={{
                    "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700 },
                    "& .Mui-selected": { color: "#f97316", backgroundColor: "rgba(249, 115, 22, 0.08)" },
                  }}
                >
                  <ToggleButton value="form" disabled={!!enrichmentJsonError}>Form</ToggleButton>
                  <ToggleButton value="json">JSON</ToggleButton>
                </ToggleButtonGroup>
                <Button startIcon={<VisibilityOutlinedIcon />} variant="outlined" onClick={handleOpenCsvPreview} sx={neutralButtonSx}>
                  Preview Feed
                </Button>
              </Stack>
            }
          >
            {importSettings ? (
              <>
                {enrichmentViewMode === "json" ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={12}
                    maxRows={30}
                    label="Enrichment Settings JSON"
                    disableValueTooltip
                    value={enrichmentJsonDraft}
                    onChange={handleEnrichmentJsonChange}
                    error={!!enrichmentJsonError}
                    helperText={enrichmentJsonError || "Edit the settings here, then select Save Settings to save your changes."}
                    sx={{ ...inputSx, "& textarea": { fontFamily: "monospace", fontSize: "0.85rem" } }}
                  />
                ) : (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Product ID Field Name"
                          name="productIdFieldName"
                          value={importSettings.productIdFieldName || ""}
                          onChange={handleImportSettingTextFieldChange}
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Title Field Name"
                          name="titleFieldName"
                          value={importSettings.titleFieldName || ""}
                          onChange={handleImportSettingTextFieldChange}
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={importSettings.hasParentProduct || false}
                              onChange={handleHasParentProductChange}
                              sx={{ color: "rgba(249, 115, 22, 0.4)", "&.Mui-checked": { color: "#f97316" } }}
                            />
                          }
                          label={<Typography sx={{ fontWeight: 700, color: "#202531" }}>Has Parent Product</Typography>}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={importSettings.usePreezieGeneratedId || false}
                              onChange={handleUsePreezieGeneratedIdChange}
                              name="usePreezieGeneratedId"
                              sx={{ color: "rgba(249, 115, 22, 0.4)", "&.Mui-checked": { color: "#f97316" } }}
                            />
                          }
                          label={<Typography sx={{ fontWeight: 700, color: "#202531" }}>Use Preezie Generated ID</Typography>}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={importSettings.useAggregationValueAsDocumentId || false}
                              onChange={handleUseAggregationValueAsDocumentIdChange}
                              name="useAggregationValueAsDocumentId"
                              sx={{ color: "rgba(249, 115, 22, 0.4)", "&.Mui-checked": { color: "#f97316" } }}
                            />
                          }
                          label={<Typography sx={{ fontWeight: 700, color: "#202531" }}>Use Aggregation Value As Document ID</Typography>}
                        />
                      </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 800, color: "#202531" }}>Rules</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {(importSettings.rules || []).length > 0 && (
                          <>
                            <TextField
                              size="small"
                              placeholder="Product ID (optional)"
                              value={testRulesProductId}
                              onChange={(event) => setTestRulesProductId(event.target.value)}
                              sx={{ width: 240, flexShrink: 0, ...inputSx }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleTestRules}
                              disabled={testRulesLoading}
                              sx={{
                                ...primaryButtonSx,
                                minWidth: 112,
                                px: 2,
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {testRulesLoading ? <CircularProgress size={14} color="inherit" /> : "Test Rules"}
                            </Button>
                          </>
                        )}
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={handleAddRule}
                          variant="outlined"
                          size="small"
                          sx={{
                            ...neutralButtonSx,
                            minWidth: 112,
                            px: 2,
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Add Rule
                        </Button>
                      </Stack>
                    </Stack>

                    {(importSettings.rules || []).map((rule, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: "16px",
                          border: "1px solid rgba(255, 132, 76, 0.10)",
                          backgroundColor: "rgba(255, 247, 241, 0.54)",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "#202531" }}>
                            Rule {index + 1}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {testRulesResults && testRulesResults[index] && (
                              <Chip
                                size="small"
                                label={(() => {
                                  const text = testRulesResults[index].error || testRulesResults[index].value || "";
                                  return text.length > 20 ? `${text.slice(0, 20)}...` : text;
                                })()}
                                color={testRulesResults[index].error ? "error" : "success"}
                                onClick={() => {
                                  setRuleDetailData(createEnrichmentRuleDetail(rule, testRulesResults[index], index, testRulesRawProduct));
                                  setRuleDetailOpen(true);
                                }}
                                sx={{ maxWidth: 160, cursor: "pointer", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveRule(index)}
                              sx={{ color: "#ef4444", width: 28, height: 28 }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Grid container spacing={1.5} alignItems="flex-start">
                          <Grid
                            item
                            xs={12}
                            sm={rule.type !== "Aggregation" && rule.type !== "ParentProductAggregation" ? 6 : 3}
                            md={rule.type !== "Aggregation" && rule.type !== "ParentProductAggregation" ? 5 : 2}
                          >
                            <Stack spacing={1.5} data-testid={`rule-field-settings-${index}`}>
                              <FormControl fullWidth size="small" sx={inputSx}>
                                <InputLabel id={`rule-type-label-${index}`}>Type</InputLabel>
                                <Select
                                  labelId={`rule-type-label-${index}`}
                                  value={rule.type || ""}
                                  label="Type"
                                  onChange={(e) => handleRuleChange(index, "type", e.target.value)}
                                >
                                  <MenuItem value="Aggregation" disabled={isAggregationRulePresent && rule.type !== "Aggregation"}>
                                    Aggregation
                                  </MenuItem>
                                  <MenuItem value="FieldValue">FieldValue</MenuItem>
                                  <MenuItem value="ParentProductAggregation" disabled={!importSettings.hasParentProduct}>
                                    ParentProductAggregation
                                  </MenuItem>
                                </Select>
                              </FormControl>
                              {rule.type !== "Aggregation" && rule.type !== "ParentProductAggregation" && (
                                <Autocomplete
                                  size="small"
                                  options={availableFieldNames || []}
                                  value={rule.fieldName || null}
                                  onChange={(event, newValue) => handleRuleChange(index, "fieldName", newValue)}
                                  renderInput={(params) => <TextField {...params} label="Field Name" sx={inputSx} />}
                                />
                              )}
                            </Stack>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={rule.type !== "Aggregation" && rule.type !== "ParentProductAggregation" ? 6 : 9}
                            md={rule.type !== "Aggregation" && rule.type !== "ParentProductAggregation" ? 7 : 10}
                          >
                            <TextField
                              size="small"
                              label="Script"
                              fullWidth
                              multiline
                              minRows={2}
                              value={rule.script || ""}
                              onChange={(e) => handleRuleChange(index, "script", e.target.value)}
                              sx={inputSx}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </>
                )}

                <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ mt: 2 }}>
                  <Button variant="outlined" onClick={handleResetImportSettings} disabled={!isImportSettingsDirty && !enrichmentJsonError} sx={neutralButtonSx}>
                    Reset
                  </Button>
                  <Button variant="contained" onClick={handleSaveImportSettings} disabled={!isImportSettingsDirty || !!enrichmentJsonError} sx={primaryButtonSx}>
                    Save Settings
                  </Button>
                </Stack>
              </>
            ) : (
              <Typography sx={{ color: "#6b7280" }}>Loading enrichment settings...</Typography>
            )}
          </SectionView>
        );
      case "feature-flags":
        return (
          <SectionView title="Feature Flags" description="AI behavior, scraping, stock rules, and storefront logic." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <Grid container spacing={2}>
              {booleanFeatureKeys.map((key) => (
                <Grid item xs={12} md={6} lg={4} key={key}>
                  <ConfigField label={prettifyKey(key)} path={`tenantAiProfile.featureConfigurations.${key}`} value={featureConfigurations[key]} onValueChange={updateValue} searchQuery={searchQuery} />
                </Grid>
              ))}
            </Grid>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                borderRadius: "20px !important",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                backgroundColor: "rgba(255,255,255,0.72)",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700, color: "#202531" }}>Advanced feature values</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ConfigField
                      label="Semantic Search Threshold"
                      path="tenantAiProfile.featureConfigurations.SemanticSearchThreshold"
                      value={semanticSearchThreshold}
                      onValueChange={updateValue}
                      searchQuery={searchQuery}
                      helperText="Use level-two semantic results when level one returns fewer than this number. Set to 0 to disable."
                      forceSingleLine
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  {textFeatureKeys.map((key) => (
                    <Grid item xs={12} md={6} key={key}>
                      <ConfigField label={prettifyKey(key)} path={`tenantAiProfile.featureConfigurations.${key}`} value={featureConfigurations[key]} onValueChange={updateValue} searchQuery={searchQuery} />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </SectionView>
        );
      case "merchandising":
        return (
          <SectionView
            title="Product Discovery"
            description="Control how products are merchandised, sorted, and refined in the widget."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
          >
            <ProductDiscoverySection
              tenantDetails={tenantDetails}
              setTenantDetails={setTenantDetails}
              onCountChange={setProductDiscoveryCount}
              primaryButtonSx={primaryButtonSx}
              neutralButtonSx={neutralButtonSx}
            />
          </SectionView>
        );
      case "data-index":
        return (
          <SectionView
            title="Data and Indexing"
            description="Connector setup, feeds, indexes, and operational toggles."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
            action={
              <Tooltip title="Preview current feed">
                <span>
                  <Button startIcon={<VisibilityOutlinedIcon />} variant="outlined" onClick={handleOpenCsvPreview} sx={neutralButtonSx}>
                    Preview Feed
                  </Button>
                </span>
              </Tooltip>
            }
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <ConfigField label="Elastic Index Name" path="tenantAiProfile.elasticIndexName" value={tenantDetails?.tenantAiProfile?.elasticIndexName || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigField label="Policy Index Name" path="tenantAiProfile.policyIndexName" value={tenantDetails?.tenantAiProfile?.policyIndexName || ""} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigField label="Web Scrape Index Name" path="tenantAiProfile.webScrapeIndexName" value={tenantDetails?.tenantAiProfile?.webScrapeIndexName || ""} onValueChange={updateValue} searchQuery={searchQuery} forceSingleLine />
              </Grid>
              <Grid item xs={12} md={3}>
                <ConfigField
                  label="Data Feed"
                  path="dataFeedConnector.configurations.dataFeed"
                  value={tenantDetails?.dataFeedConnector?.configurations?.dataFeed || ""}
                  onValueChange={updateDataFeed}
                  searchQuery={searchQuery}
                  options={[
                    { value: "Shopify", label: "Shopify" },
                    { value: "Http", label: "Http" },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ConfigField
                  label="Data Type"
                  path="dataFeedConnector.configurations.dataType"
                  value={isShopifyDataFeed ? "JSON" : tenantDetails?.dataFeedConnector?.configurations?.dataType || ""}
                  onValueChange={updateValue}
                  searchQuery={searchQuery}
                  disabled={isShopifyDataFeed}
                  options={[
                    { value: "JSON", label: "JSON" },
                    { value: "CSV", label: "CSV" },
                    { value: "XML", label: "XML" },
                    { value: "txt", label: "TXT" },
                  ]}
                />
              </Grid>
              {isShopifyDataFeed ? (
                <>
                  <Grid item xs={12} md={4}>
                    <ConfigField label="App install status" path="dataFeedConnector.configurations.appInstallStatus" value={shopifyAppInstallStatus} inputProps={{ style: { color: shopifyAppInstallStatus === "Approved" ? "#15803d" : undefined, fontWeight: shopifyAppInstallStatus === "Approved" ? 700 : undefined } }} onValueChange={updateValue} searchQuery={searchQuery} forceSingleLine />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <ConfigField label="Shopify Redirect URL" path="dataFeedConnector.configurations.shopifyRedirectUrl" value={shopifyRedirectUrl} onValueChange={updateValue} searchQuery={searchQuery} forceSingleLine />
                  </Grid>
                  <Grid item xs={12}>
                    <ConfigField label="Shopify App URL" path="dataFeedConnector.configurations.shopifyAppUrl" value={shopifyAppUrl} onValueChange={updateValue} searchQuery={searchQuery} forceSingleLine />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Data Feed URL"
                    value={tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl || ""}
                    onChange={(e) => updateValue("dataFeedConnector.configurations.dataFeedUrl", e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Pick from assets">
                            <IconButton
                              size="small"
                              onClick={() => setAssetPickerTarget({ path: "dataFeedConnector.configurations.dataFeedUrl" })}
                              sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,247,241,0.9)" } }}
                            >
                              <FolderOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <ConfigField label="Display Size Override" path="tenantAiProfile.displaySizeOverride" value={tenantDetails?.tenantAiProfile?.displaySizeOverride} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ConfigField label="Knowledge Base Max Docs" path="tenantAiProfile.knowledgeBaseMaxDocs" value={tenantDetails?.tenantAiProfile?.knowledgeBaseMaxDocs ?? 0} onValueChange={updateValue} searchQuery={searchQuery} />
              </Grid>
              {matchesSearch(`Include in Nightly Update (${toBoolean(tenantDetails?.tenantAiProfile?.nightlyProductUpdate) ? "ON" : "OFF"})`, tenantDetails?.tenantAiProfile?.nightlyProductUpdate, searchQuery) && (
                <Grid item xs={12} md={4}>
                  <NightlyProductUpdateToggle
                    key={tenantDetails?.id}
                    tenantId={tenantDetails?.id}
                    value={tenantDetails?.tenantAiProfile?.nightlyProductUpdate}
                    disabled={savingTenant || savingNightlyProductUpdate}
                    onSavingChange={setSavingNightlyProductUpdate}
                    onSaved={(tenantId, isActive) => setTenantDetails((previous) =>
                      previous?.id === tenantId
                        ? cloneWithUpdatedValue(previous, "tenantAiProfile.nightlyProductUpdate", isActive)
                        : previous
                    )}
                  />
                </Grid>
              )}
            </Grid>
            <DynamicFieldGrid objectValue={tenantDetails?.dataFeedConnector?.configurations} basePath="dataFeedConnector.configurations" excludeKeys={["dataFeed", "dataType", "dataFeedUrl", "appInstallStatus", "shopifyAppUrl", "shopifyRedirectUrl", "useGraphQLBulkQuery", "useGraphQL", "EnableProductSalesCount", "ProductSalesLookbackDays"]} searchQuery={searchQuery} onValueChange={updateValue} />
            <Box
              component="fieldset"
              sx={{
                m: 0,
                mt: 2,
                px: 2,
                pb: 2,
                border: "1px solid rgba(15, 23, 42, 0.14)",
                borderRadius: "18px",
              }}
            >
              <Box
                component="legend"
                sx={{
                  px: 0.75,
                  color: "#6b7280",
                  fontSize: "0.875rem",
                }}
              >
                Metafields
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ConfigField
                    label="Use GraphQL Bulk Query"
                    path="dataFeedConnector.configurations.useGraphQLBulkQuery"
                    value={String(tenantDetails?.dataFeedConnector?.configurations?.useGraphQLBulkQuery ?? false)}
                    onValueChange={updateGraphQLToggle}
                    searchQuery={searchQuery}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ConfigField
                    label="Use GraphQL API"
                    path="dataFeedConnector.configurations.useGraphQL"
                    value={String(tenantDetails?.dataFeedConnector?.configurations?.useGraphQL ?? false)}
                    onValueChange={updateGraphQLToggle}
                    searchQuery={searchQuery}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box
              component="fieldset"
              sx={{
                m: 0,
                mt: 2,
                px: 2,
                pb: 2,
                border: "1px solid rgba(15, 23, 42, 0.14)",
                borderRadius: "18px",
              }}
            >
              <Box
                component="legend"
                sx={{
                  px: 0.75,
                  color: "#6b7280",
                  fontSize: "0.875rem",
                }}
              >
                Importer Merchandising
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ConfigField
                    label="Enable Product Sales Count"
                    path="tenantAiProfile.featureConfigurations.EnableProductSalesCount"
                    value={String(
                      featureConfigurations.EnableProductSalesCount
                      ?? dataFeedConfigurations.EnableProductSalesCount
                      ?? true
                    )}
                    onValueChange={updateValue}
                    searchQuery={searchQuery}
                    helperText="Include product sales counts during import."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {matchesSearch(
                    "Product Sales Lookback Days",
                    featureConfigurations.ProductSalesLookbackDays
                      ?? dataFeedConfigurations.ProductSalesLookbackDays
                      ?? "30",
                    searchQuery
                  ) ? (
                    <TextField
                      fullWidth
                      label="Product Sales Lookback Days"
                      type="number"
                      value={String(
                        featureConfigurations.ProductSalesLookbackDays
                        ?? dataFeedConfigurations.ProductSalesLookbackDays
                        ?? "30"
                      )}
                      onChange={(event) => updateProductSalesLookbackDays(
                        "tenantAiProfile.featureConfigurations.ProductSalesLookbackDays",
                        event.target.value
                      )}
                      onBlur={(event) => normaliseProductSalesLookbackDays(
                        "tenantAiProfile.featureConfigurations.ProductSalesLookbackDays",
                        event.target.value
                      )}
                      inputProps={{ min: 30, max: 60, step: 1 }}
                      helperText="Between 30 and 60 days."
                      sx={inputSx}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </Box>
            {isShopifyDataFeed && matchesSearch("Manual secret setup Enter Client ID Enter Client Secret Save client and secret", "", searchQuery) && (
              <Box component="fieldset" sx={{ m: 0, mt: 2, px: 2, pb: 2, border: "1px solid rgba(15, 23, 42, 0.14)", borderRadius: "18px" }}>
                <Box component="legend" sx={{ px: 0.75, color: "#6b7280", fontSize: "0.875rem" }}>
                  Manual secret setup
                </Box>
                <ShopifyClientCredentials key={tenantDetails?.id} tenantId={tenantDetails?.id} onSaved={onShopifyCredentialsSaved} buttonSx={primaryButtonSx} inputSx={inputSx} />
              </Box>
            )}
          </SectionView>
        );
      case "metafields":
        return (
          <SectionView
            title="Metafields"
            description="Select Shopify metafields to make available in your widget."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
          >
            {(!isGraphQLBulkQueryEnabled && !isGraphQLEnabled) && (
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.25,
                  borderRadius: "14px",
                  border: "1px solid rgba(234, 88, 12, 0.25)",
                  backgroundColor: "rgba(255, 247, 237, 0.9)",
                }}
              >
                <Typography variant="body2" sx={{ color: "#9a3412" }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>GraphQL configuration is disabled. </Box>
                  Enable Use GraphQL API or Use GraphQL Bulk Query in Data &amp; Indexing before configuring metafields.
                </Typography>
              </Paper>
            )}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                px: 2,
                py: 1.25,
                borderRadius: "14px",
                border: "1px solid rgba(249, 115, 22, 0.22)",
                backgroundColor: "rgba(255, 247, 237, 0.78)",
              }}
            >
              <Typography variant="body2" sx={{ color: "#9a3412" }}>
                <Box component="span" sx={{ fontWeight: 800 }}>TenantConfig save format: </Box>
                metafields are displayed as <Box component="span" sx={{ fontFamily: "monospace" }}>namespace.key</Box>, then saved as{" "}
                <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>namespace__key</Box>. For example,{" "}
                <Box component="span" sx={{ fontFamily: "monospace" }}>custom.color</Box> becomes <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>custom__color</Box>.
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: "16px", border: "1px solid rgba(15, 23, 42, 0.1)" }}>
              <Typography sx={{ fontWeight: 800, color: "#202531", mb: 0.5 }}>Preview product metafields</Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
                Check the exact metafield values returned for one Shopify product in the selected tenant.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Product ID"
                  value={productMetafieldId}
                  onChange={(event) => setProductMetafieldId(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && metafieldsTab === "product") {
                      event.preventDefault();
                      fetchProductMetafieldDefinitions();
                    }
                  }}
                  placeholder="519720599585"
                  sx={inputSx}
                />
                <Button
                  variant="outlined"
                  disabled={!productMetafieldId.trim() || loadingProductMetafieldPreview}
                  onClick={previewProductMetafields}
                  sx={{ minWidth: 100, borderRadius: "12px", textTransform: "none", fontWeight: 700, borderColor: "#f97316", color: "#ea580c", "&:hover": { borderColor: "#ea580c", backgroundColor: "#fff7ed" } }}
                >
                  {loadingProductMetafieldPreview ? "Previewing…" : "Preview"}
                </Button>
              </Stack>
              {productMetafieldPreviewError && (
                <Typography role="alert" variant="body2" sx={{ color: "#b91c1c", mt: 1 }}>
                  {productMetafieldPreviewError}
                </Typography>
              )}
            </Paper>
            <Tabs
              value={metafieldsTab}
              onChange={(_, nextTab) => setMetafieldsTab(nextTab)}
              sx={{ mb: 2, borderBottom: "1px solid rgba(15, 23, 42, 0.1)" }}
            >
              <Tab value="definitions" label="Metafield definitions" sx={{ textTransform: "none", fontWeight: 700 }} />
              <Tab value="product" label="By product ID" sx={{ textTransform: "none", fontWeight: 700 }} />
            </Tabs>

            {metafieldsTab === "product" && (
              <Paper elevation={0} sx={{ mb: 2, p: 2, borderRadius: "16px", border: "1px solid rgba(15, 23, 42, 0.1)" }}>
                <Typography sx={{ fontWeight: 800, color: "#202531", mb: 0.5 }}>Get metafields by product ID</Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
                  Load the metafields available on the Product ID above for the selected tenant.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    disabled={!productMetafieldId.trim() || loadingProductMetafields}
                    onClick={fetchProductMetafieldDefinitions}
                    sx={{ minWidth: 100, borderRadius: "12px", textTransform: "none", fontWeight: 700, backgroundColor: "#f97316", "&:hover": { backgroundColor: "#ea580c" } }}
                  >
                    {loadingProductMetafields ? "Loading…" : "Load"}
                  </Button>
                </Stack>
              </Paper>
            )}

            <Dialog
              open={productMetafieldPreviewOpen}
              onClose={() => setProductMetafieldPreviewOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: "20px" } }}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 800, color: "#202531" }}>Product metafields preview</Typography>
                <IconButton aria-label="Close product metafields preview" onClick={() => setProductMetafieldPreviewOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    maxHeight: "60vh",
                    overflow: "auto",
                    borderRadius: "12px",
                    backgroundColor: "#111827",
                    color: "#f9fafb",
                    fontFamily: "monospace",
                    fontSize: "0.8125rem",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  <ProductMetafieldPreviewJson value={productMetafieldPreview} />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setProductMetafieldPreviewOpen(false)} sx={{ color: "#ea580c", textTransform: "none", fontWeight: 700 }}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MetafieldPanel
                  title="Product Metafields"
                  definitionGroups={metafieldsTab === "product" ? productMetafieldDefinitions.product : metafieldDefinitions.product}
                  isLoading={metafieldsTab === "product" ? loadingProductMetafields : loadingMetafields}
                  error={metafieldsTab === "product" ? productMetafieldsError : metafieldsError}
                  onRetry={metafieldsTab === "product" ? fetchProductMetafieldDefinitions : fetchMetafieldDefinitions}
                  value={featureConfigurations.ProductMetafields}
                  onValueChange={(nextValue) => updateMetafieldSelection("ProductMetafields", nextValue)}
                  requiredValues={requiredProductMetafieldValues}
                  searchQuery={searchQuery}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MetafieldPanel
                  title="Variant Metafields"
                  definitionGroups={metafieldsTab === "product" ? productMetafieldDefinitions.variant : metafieldDefinitions.variant}
                  isLoading={metafieldsTab === "product" ? loadingProductMetafields : loadingMetafields}
                  error={metafieldsTab === "product" ? productMetafieldsError : metafieldsError}
                  onRetry={metafieldsTab === "product" ? fetchProductMetafieldDefinitions : fetchMetafieldDefinitions}
                  value={featureConfigurations.VariantMetafields}
                  onValueChange={(nextValue) => updateValue("tenantAiProfile.featureConfigurations.VariantMetafields", nextValue)}
                  searchQuery={searchQuery}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MetafieldPanel
                  title="Size Chart Metafields"
                  definitionGroups={metafieldsTab === "product" ? productMetafieldDefinitions.product : metafieldDefinitions.product}
                  isLoading={metafieldsTab === "product" ? loadingProductMetafields : loadingMetafields}
                  error={metafieldsTab === "product" ? productMetafieldsError : metafieldsError}
                  onRetry={metafieldsTab === "product" ? fetchProductMetafieldDefinitions : fetchMetafieldDefinitions}
                  value={featureConfigurations.SizeChartMetafield}
                  onValueChange={(nextValue) => updateMetafieldSelection("SizeChartMetafield", nextValue)}
                  searchQuery={searchQuery}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MetafieldPanel
                  title="Sizing Information"
                  definitionGroups={metafieldsTab === "product" ? productMetafieldDefinitions.product : metafieldDefinitions.product}
                  isLoading={metafieldsTab === "product" ? loadingProductMetafields : loadingMetafields}
                  error={metafieldsTab === "product" ? productMetafieldsError : metafieldsError}
                  onRetry={metafieldsTab === "product" ? fetchProductMetafieldDefinitions : fetchMetafieldDefinitions}
                  value={featureConfigurations.SizeInformationMetafields}
                  onValueChange={(nextValue) => updateMetafieldSelection("SizeInformationMetafields", nextValue)}
                  searchQuery={searchQuery}
                />
              </Grid>
            </Grid>
          </SectionView>
        );
      case "taxonomy":
        return (
          <SectionView
            title="Taxonomy"
            description="Choose the Shopify taxonomy categories configured for this tenant."
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
          >
            <TenantTaxonomySection
              tenantDetails={tenantDetails}
              setTenantDetails={setTenantDetails}
            />
          </SectionView>
        );
      case "widget-settings":
        return (
          <SectionView title="Widget Settings" description="Trigger behavior, CTA settings, and quick actions." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {["triggerTitle", "triggerPosition", "triggerAlign", "triggerType", "tenantUtmSource", "priceFormatPattern"].map((key) => (
                <Grid item xs={12} md={6} lg={4} key={key}>
                  <ConfigField label={prettifyKey(key)} path={`tenantAiProfile.widgetConfig.settingsConfig.${key}`} value={widgetSettings[key] ?? ""} onValueChange={updateValue} searchQuery={searchQuery} />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2}>
              {booleanWidgetKeys.map((key) => (
                <Grid item xs={12} md={6} lg={4} key={key}>
                  <ConfigField label={prettifyKey(key)} path={`tenantAiProfile.widgetConfig.settingsConfig.${key}`} value={widgetSettings[key]} onValueChange={updateValue} searchQuery={searchQuery} />
                </Grid>
              ))}
            </Grid>
            {advancedWidgetKeys.length > 0 ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: "20px !important",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  backgroundColor: "rgba(255,255,255,0.72)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700, color: "#202531" }}>Advanced widget values</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {advancedWidgetKeys.map((key) => (
                      <Grid item xs={12} md={6} key={key}>
                        <ConfigField label={prettifyKey(key)} path={`tenantAiProfile.widgetConfig.settingsConfig.${key}`} value={widgetSettings[key]} onValueChange={updateValue} searchQuery={searchQuery} />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ) : null}
          </SectionView>
        );
      case "widget-theme":
        return (
          <SectionView title="Widget Theme" description="Global widget tokens, colors, radius, and spacing." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <DynamicFieldGrid compact objectValue={themeConfig} basePath="tenantAiProfile.widgetConfig.themeConfig" searchQuery={searchQuery} onValueChange={updateValue} />
          </SectionView>
        );
      case "pdp-theme":
        return (
          <SectionView title="PDP Theme" description="Product page widget styling and page-specific theme tokens." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <DynamicFieldGrid compact objectValue={pdpThemeConfig} basePath="tenantAiProfile.widgetConfig.pdpThemeConfig" searchQuery={searchQuery} onValueChange={updateValue} />
          </SectionView>
        );
      case "advanced":
        return (
          <SectionView title="Metadata" description="IDs, dates, and publishing details." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Tenant ID" value={tenantDetails?.id || ""} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="AI Profile ID" value={tenantDetails?.tenantAiProfile?.id || ""} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Created At" value={tenantDetails?.createDate || ""} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Updated At" value={tenantDetails?.updateDate || ""} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Published At" value={tenantDetails?.publishedAt || ""} InputProps={{ readOnly: true }} sx={inputSx} />
              </Grid>
            </Grid>
          </SectionView>
        );
      case "tenant-json":
        return (
          <SectionView title="Tenant JSON" description="Fallback JSON editing for the full tenant configuration." icon={activeSectionMeta.icon} showCount={false}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button variant="outlined" disabled={!tenantJsonEditorReady} onClick={() => runTenantJsonAction("editor.foldAll")} sx={neutralButtonSx}>
                  Collapse all
                </Button>
                <Button variant="outlined" disabled={!tenantJsonEditorReady} onClick={() => runTenantJsonAction("editor.unfoldAll")} sx={neutralButtonSx}>
                  Expand all
                </Button>
              </Stack>
              <MuiTextField
                fullWidth
                size="small"
                label="Search Tenant JSON"
                placeholder="Find keys or values"
                value={tenantJsonSearch}
                onChange={(event) => setTenantJsonSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    navigateTenantJsonMatch(event.shiftKey ? -1 : 1);
                  } else if (event.key === "Escape") {
                    setTenantJsonSearch("");
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8" }} /></InputAdornment>,
                  endAdornment: tenantJsonSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear Tenant JSON search" onClick={() => setTenantJsonSearch("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputSx, flex: 1, minWidth: 0 }}
              />
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Typography role="status" variant="body2" sx={{ color: "#6b7280", minWidth: 80, textAlign: "center" }}>
                  {tenantJsonSearch ? (tenantJsonMatches.length ? `${tenantJsonMatchIndex + 1} of ${tenantJsonMatches.length}` : "No matches") : ""}
                </Typography>
                <IconButton aria-label="Previous JSON match" disabled={!tenantJsonMatches.length} onClick={() => navigateTenantJsonMatch(-1)}>
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton aria-label="Next JSON match" disabled={!tenantJsonMatches.length} onClick={() => navigateTenantJsonMatch(1)}>
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Box sx={{
              border: "1px solid rgba(255, 132, 76, 0.18)", borderRadius: "12px", overflow: "hidden",
              "& .tenant-json-search-match": { backgroundColor: "rgba(250, 204, 21, 0.3)", borderRadius: "2px" },
              "& .tenant-json-current-match": { backgroundColor: "rgba(249, 115, 22, 0.35)", outline: "1px solid #f97316", borderRadius: "2px" },
            }}>
              <Editor
                height="560px"
                language="json"
                theme="vs"
                value={tenantJsonDraft}
                onChange={handleTenantJsonChange}
                onMount={(editor) => {
                  tenantJsonEditorRef.current = editor;
                  tenantJsonSearchDecorationsRef.current = editor.createDecorationsCollection();
                  setTenantJsonEditorReady(true);
                }}
                options={{
                  ariaLabel: "Tenant JSON editor",
                  automaticLayout: true,
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineHeight: 22,
                  tabSize: 2,
                  insertSpaces: true,
                  folding: true,
                  showFoldingControls: "always",
                  bracketPairColorization: { enabled: true },
                  guides: { bracketPairs: true, indentation: true },
                  formatOnPaste: true,
                  scrollBeyondLastLine: false,
                  padding: { top: 12, bottom: 12 },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 1.25 }}>
              Use the arrows beside the line numbers to collapse or expand objects and arrays. Save Changes applies your edits.
            </Typography>
            {tenantJsonError && (
              <Typography role="alert" color="error" variant="body2" sx={{ mt: 1 }}>
                {tenantJsonError}
              </Typography>
            )}
          </SectionView>
        );
      case "caches":
        return (
          <SectionView
            title="Caches"
            description={`Clear cached data for this tenant. ${(tenantDetails?.id || "").toUpperCase()}`}
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
            action={
              <Button
                startIcon={clearingTenantCache ? <CircularProgress size={16} color="inherit" /> : <CachedOutlinedIcon />}
                variant="contained"
                onClick={handleClearTenantCache}
                disabled={clearingTenantCache}
                sx={primaryButtonSx}
              >
                Clear Tenant Cache
              </Button>
            }
          >
            {cacheMessage && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: cacheMessage.type === "success" ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)", border: `1px solid ${cacheMessage.type === "success" ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"}` }}>
                <Typography variant="body2" sx={{ color: cacheMessage.type === "success" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                  {cacheMessage.text}
                </Typography>
                <IconButton size="small" onClick={() => setCacheMessage(null)} sx={{ color: cacheMessage.type === "success" ? "#16a34a" : "#dc2626" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {loadingCacheCounts && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: "#6b7280" }}>Loading cache counts...</Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                size="small"
                startIcon={loadingCacheCounts ? <CircularProgress size={14} color="inherit" /> : <CachedOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={fetchCacheCounts}
                disabled={loadingCacheCounts}
                sx={{ color: "#f97316", textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
              >
                Refresh Counts
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 247, 241, 0.96), rgba(255,255,255,0.92))" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#202531" }}>
                      Greetings Cache
                    </Typography>
                    {cacheCounts != null && (
                      <Chip label={`${cacheCounts.greeting ?? 0} entries`} size="small" sx={{ backgroundColor: "rgba(249, 115, 22, 0.1)", color: "#f97316", fontWeight: 700, height: 22, fontSize: "0.75rem" }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={handleLoadGreetingEntries}
                      disabled={loadingGreetingEntries}
                      sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}
                    >
                      {loadingGreetingEntries ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                    Clear all greetings cache for tenant
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setClearCacheConfirm("greetings")}
                    disabled={clearingCacheType === "greetings"}
                    sx={primaryButtonSx}
                  >
                    {clearingCacheType === "greetings" ? <CircularProgress size={16} color="inherit" /> : "Clear Greetings Cache"}
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 247, 241, 0.96), rgba(255,255,255,0.92))" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#202531" }}>
                      Clear greeting cache targeting Product/Url
                    </Typography>
                    {cacheCounts != null && (
                      <Chip label={`${cacheCounts.product ?? 0} entries`} size="small" sx={{ backgroundColor: "rgba(249, 115, 22, 0.1)", color: "#f97316", fontWeight: 700, height: 22, fontSize: "0.75rem" }} />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                    Enter a product ID or URL to clear its specific cache entry
                  </Typography>
                  <RadioGroup
                    row
                    value={productCacheTarget}
                    onChange={(e) => setProductCacheTarget(e.target.value)}
                    sx={{ mb: 1.5, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                  >
                    <FormControlLabel value="product" control={<Radio size="small" />} label="Product ID" />
                    <FormControlLabel value="url" control={<Radio size="small" />} label="URL" />
                  </RadioGroup>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={productCacheTarget === "product" ? "Enter Product ID" : "Enter URL"}
                    value={productKey}
                    onChange={(e) => setProductKey(e.target.value)}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#fffaf7",
                        "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                        "&:hover fieldset": { borderColor: "#f97316" },
                        "&.Mui-focused fieldset": { borderColor: "#f97316" },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setClearCacheConfirm("greetingTarget")}
                    disabled={clearingCacheType === "greetingTarget" || !productKey.trim()}
                    sx={primaryButtonSx}
                  >
                    {clearingCacheType === "greetingTarget" ? <CircularProgress size={16} color="inherit" /> : "Clear Cache"}
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 250, 245, 0.98), rgba(255,255,255,0.92))" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#202531" }}>
                      Custom Cache Key
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleLoadCustomEntries}
                      disabled={loadingCustomEntries}
                      sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}
                    >
                      {loadingCustomEntries ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                    Clear cache entries that start with a custom prefix.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <TextField
                      size="small"
                      placeholder="Keys that start with..."
                      value={customCachePrefix}
                      onChange={(e) => setCustomCachePrefix(e.target.value)}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#fffaf7",
                          "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                          "&:hover fieldset": { borderColor: "#f97316" },
                          "&.Mui-focused fieldset": { borderColor: "#f97316" },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531", whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                              {(tenantDetails?.id || "").toUpperCase()} :
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setClearCacheConfirm("custom")}
                      disabled={clearingCacheType === "custom" || !customCachePrefix.trim()}
                      sx={primaryButtonSx}
                    >
                      {clearingCacheType === "custom" ? <CircularProgress size={16} color="inherit" /> : "Clear"}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            <Dialog
              open={greetingEntriesOpen}
              onClose={() => { setGreetingEntriesOpen(false); setSelectedGreetingEntry(null); }}
              fullWidth
              maxWidth="lg"
              PaperProps={{
                sx: {
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 132, 76, 0.12)",
                  background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
                  boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
                },
              }}
            >
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
                  {greetingEntriesSource === "custom" ? "Cache Entries" : "Greeting Cache Entries"}
                </Typography>
                <IconButton onClick={() => { setGreetingEntriesOpen(false); setSelectedGreetingEntry(null); }} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 2, pb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Filter by key or value..."
                    value={greetingEntriesFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      setGreetingEntriesFilter(val);
                      const n = val.toLowerCase();
                      const matches = n ? greetingEntries.filter((entry) => {
                        const k = (entry.key ?? entry.Key ?? "").toLowerCase();
                        const v = (entry.value ?? entry.Value ?? "").toLowerCase();
                        return k.includes(n) || v.includes(n);
                      }) : greetingEntries;
                      setSelectedGreetingEntry(matches.length === 1 ? 0 : null);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#fffaf7",
                        "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" },
                        "&:hover fieldset": { borderColor: "#f97316" },
                        "&.Mui-focused fieldset": { borderColor: "#f97316" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#f97316" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {(() => {
                  const needle = greetingEntriesFilter.toLowerCase();
                  const filtered = needle
                    ? greetingEntries.filter((e) => {
                      const k = (e.key ?? e.Key ?? "").toLowerCase();
                      const v = (e.value ?? e.Value ?? "").toLowerCase();
                      return k.includes(needle) || v.includes(needle);
                    })
                    : greetingEntries;
                  if (filtered.length === 0) {
                    return (
                      <Typography variant="body2" sx={{ color: "#6b7280", p: 3, textAlign: "center" }}>
                        {greetingEntries.length === 0 ? "No greeting cache entries found." : "No entries match the filter."}
                      </Typography>
                    );
                  }
                  const renderValue = (entry) => {
                    const raw = entry.value ?? entry.Value;
                    try {
                      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                      return JSON.stringify(typeof parsed === "string" ? JSON.parse(parsed) : parsed, null, 2);
                    } catch {
                      return raw;
                    }
                  };
                  return (
                    <Box
                      ref={greetingContainerRef}
                      sx={{ display: "flex", height: "60vh", overflow: "hidden", userSelect: greetingDragging.current ? "none" : "auto" }}
                    >
                      {/* Left panel — Keys */}
                      <Box sx={{ width: `${greetingSplitPercent}%`, display: "flex", flexDirection: "column", borderRight: "none", minWidth: 0 }}>
                        <Box sx={{ px: 1.5, py: 1, backgroundColor: "rgba(255, 247, 241, 0.8)", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#202531", textTransform: "uppercase", letterSpacing: "0.05em" }}>Keys ({filtered.length})</Typography>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                          {filtered.map((entry, idx) => {
                            const key = entry.key ?? entry.Key;
                            const isSelected = selectedGreetingEntry === idx;
                            return (
                              <Box
                                key={idx}
                                onClick={() => setSelectedGreetingEntry(idx)}
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  backgroundColor: isSelected ? "rgba(249, 115, 22, 0.10)" : idx % 2 === 0 ? "transparent" : "rgba(255, 247, 241, 0.4)",
                                  borderLeft: isSelected ? "3px solid #f97316" : "3px solid transparent",
                                  borderBottom: "1px solid rgba(255, 132, 76, 0.06)",
                                  "&:hover": { backgroundColor: isSelected ? "rgba(249, 115, 22, 0.10)" : "rgba(255, 247, 241, 0.6)" },
                                  transition: "background-color 0.15s, border-left 0.15s",
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 600, color: isSelected ? "#f97316" : "#202531", fontSize: "0.82rem", wordBreak: "break-all", flex: 1, minWidth: 0 }}>
                                  {key}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setDeleteConfirmKey(key); }}
                                  sx={{ ml: 0.5, flexShrink: 0, color: "#ef4444", "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" } }}
                                >
                                  <DeleteIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Draggable divider */}
                      <Box
                        onMouseDown={handleSplitMouseDown}
                        sx={{
                          width: "6px",
                          cursor: "col-resize",
                          backgroundColor: "rgba(255, 132, 76, 0.12)",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": { backgroundColor: "rgba(249, 115, 22, 0.25)" },
                          transition: "background-color 0.15s",
                        }}
                      >
                        <Box sx={{ width: "2px", height: "30px", borderRadius: "2px", backgroundColor: "rgba(249, 115, 22, 0.4)" }} />
                      </Box>

                      {/* Right panel — Value */}
                      <Box sx={{ width: `${100 - greetingSplitPercent}%`, display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <Box sx={{ px: 1.5, py: 1, backgroundColor: "rgba(255, 247, 241, 0.8)", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#202531", textTransform: "uppercase", letterSpacing: "0.05em" }}>Value</Typography>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                          {selectedGreetingEntry !== null && filtered[selectedGreetingEntry] ? (
                            <pre style={{ margin: 0, fontSize: "0.8rem", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#374151", lineHeight: 1.6 }}>
                              {renderValue(filtered[selectedGreetingEntry])}
                            </pre>
                          ) : (
                            <Typography variant="body2" sx={{ color: "#9ca3af", fontStyle: "italic", textAlign: "center", mt: 4 }}>
                              Select a key to view its value
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}
              </DialogContent>
            </Dialog>
            <Dialog
              open={!!deleteConfirmKey}
              onClose={() => setDeleteConfirmKey(null)}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  border: "1px solid rgba(239, 68, 68, 0.15)",
                  p: 1,
                },
              }}
            >
              <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>Delete Cache Entry</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Are you sure you want to delete this cache entry?
                </Typography>
                <Typography variant="body2" sx={{ color: "#202531", fontWeight: 600, mt: 1, wordBreak: "break-all" }}>
                  {deleteConfirmKey}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setDeleteConfirmKey(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => handleDeleteCacheKey(deleteConfirmKey)}
                  sx={{
                    backgroundColor: "#ef4444",
                    "&:hover": { backgroundColor: "#dc2626" },
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </SectionView>
        );
      case "cache-v2":
        return (
          <SectionView
            title="Cache V2"
            description={`Invalidate tag-based cache entries for ${(tenantDetails?.id || "").toUpperCase()}.`}
            icon={activeSectionMeta.icon}
            count={activeSectionMeta.count}
          >
            <CacheV2Section tenantId={tenantDetails?.id} primaryButtonSx={primaryButtonSx} />
          </SectionView>
        );
      case "jobs":
        return (
          <SectionView title="Jobs" description={`Manage recurring jobs for ${(tenantDetails?.id || "").toUpperCase()}`} icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            {jobMessage && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: jobMessage.type === "success" ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)", border: `1px solid ${jobMessage.type === "success" ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"}` }}>
                <Typography variant="body2" sx={{ color: jobMessage.type === "success" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                  {jobMessage.text}
                </Typography>
                <IconButton size="small" onClick={() => setJobMessage(null)} sx={{ color: jobMessage.type === "success" ? "#16a34a" : "#dc2626" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Tooltip title={jobsList.length >= 1 ? "Only one job allowed per tenant" : ""}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleNewJob}
                    disabled={jobsList.length >= 1}
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{
                      background: jobsList.length >= 1 ? undefined : "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                      "&:hover": { background: jobsList.length >= 1 ? undefined : "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" },
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2,
                    }}
                  >
                    New Job
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchJobs}
                disabled={loadingJobs}
                sx={{ borderColor: "rgba(255, 132, 76, 0.3)", color: "#f97316", borderRadius: "10px", textTransform: "none", fontWeight: 600, "&:hover": { borderColor: "#f97316", backgroundColor: "#fff7f1" } }}
              >
                {loadingJobs ? <CircularProgress size={16} /> : "Refresh"}
              </Button>
            </Box>
            {loadingJobs && jobsList.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} sx={{ color: "#f97316" }} />
              </Box>
            ) : jobsList.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 4 }}>
                No recurring jobs found for this tenant.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {jobsList.map((job) => (
                  <Paper key={job.id} elevation={0} sx={{ p: 2, borderRadius: "14px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 250, 245, 0.98), rgba(255,255,255,0.92))" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531", wordBreak: "break-all" }}>
                          {job.id}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mt: 0.75, flexWrap: "wrap" }}>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Schedule: <strong>{(() => {
                              const parts = (job.cron || "").split(/\s+/);
                              if (parts.length === 5) {
                                if (parts[0].startsWith("*/") && parts[1] === "*" && parts[2] === "*")
                                  return `Every ${parts[0].replace("*/", "")} Minute${parts[0].replace("*/", "") !== "1" ? "s" : ""}`;
                                if (parts[0] === "0" && parts[1].startsWith("*/") && parts[2] === "*")
                                  return `Every ${parts[1].replace("*/", "")} Hour${parts[1].replace("*/", "") !== "1" ? "s" : ""}`;
                                if (parts[0] === "0" && parts[1] === "0" && parts[2].startsWith("*/"))
                                  return `Every ${parts[2].replace("*/", "")} Day${parts[2].replace("*/", "") !== "1" ? "s" : ""}`;
                              }
                              return job.cron || "—";
                            })()}</strong>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Queue: <strong>{job.queue || "default"}</strong>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            State: <strong>{job.lastJobState || "—"}</strong>
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                          {job.nextExecution && (
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              Next: {new Date(job.nextExecution).toLocaleString()}
                            </Typography>
                          )}
                          {job.lastExecution && (
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              Last: {new Date(job.lastExecution).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        {job.error && (
                          <Typography variant="caption" sx={{ color: "#dc2626", mt: 0.5, display: "block" }}>
                            Error: {job.error}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Tooltip title="Job history">
                          <IconButton
                            size="small"
                            onClick={() => handleJobHistory(job.id)}
                            sx={{ color: "#7c3aed", "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.08)" } }}
                          >
                            <HistoryOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Trigger job">
                          <IconButton
                            size="small"
                            onClick={() => handleTriggerJob(job.id)}
                            disabled={triggeringJobId === job.id}
                            sx={{ color: "#16a34a", "&:hover": { backgroundColor: "rgba(34, 197, 94, 0.08)" } }}
                          >
                            {triggeringJobId === job.id ? <CircularProgress size={16} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit job">
                          <IconButton
                            size="small"
                            onClick={() => handleEditJob(job)}
                            sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete job">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteJobConfirm(job)}
                            sx={{ color: "#ef4444", "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Create/Edit Job Dialog */}
            <Dialog
              open={jobFormOpen}
              onClose={() => { setJobFormOpen(false); setEditingJob(null); }}
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 132, 76, 0.12)",
                  background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
                  boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
                },
              }}
            >
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
                  {editingJob ? "Edit Job" : "Create Job"}
                </Typography>
                <IconButton onClick={() => { setJobFormOpen(false); setEditingJob(null); }} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ "&.Mui-focused": { color: "#f97316" } }}>Job Type</InputLabel>
                    <Select
                      value={jobForm.jobType}
                      label="Job Type"
                      onChange={(e) => setJobForm((prev) => ({ ...prev, jobType: e.target.value }))}
                      sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" } }}
                    >
                      <MenuItem value="product-update">Product Update</MenuItem>
                      <MenuItem value="scrape-products">Scrape Products</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531" }}>Schedule</Typography>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                      {jobForm.cronUnit !== "custom" && (
                        <>
                          <Typography variant="body2" sx={{ color: "#6b7280", whiteSpace: "nowrap" }}>Every</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={jobForm.cronInterval || "1"}
                            onChange={(e) => {
                              const val = Math.max(1, Number(e.target.value) || 1).toString();
                              const unit = jobForm.cronUnit || "hours";
                              let cron = "";
                              if (unit === "minutes") cron = `*/${val} * * * *`;
                              else if (unit === "hours") cron = `0 */${val} * * *`;
                              else if (unit === "days") cron = `0 0 */${val} * *`;
                              setJobForm((prev) => ({ ...prev, cronInterval: val, cron }));
                            }}
                            inputProps={{ min: 1 }}
                            sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
                          />
                        </>
                      )}
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={jobForm.cronUnit || "hours"}
                          onChange={(e) => {
                            const unit = e.target.value;
                            if (unit === "custom") {
                              setJobForm((prev) => ({ ...prev, cronUnit: "custom" }));
                              return;
                            }
                            const val = jobForm.cronInterval || "1";
                            let cron = "";
                            if (unit === "minutes") cron = `*/${val} * * * *`;
                            else if (unit === "hours") cron = `0 */${val} * * *`;
                            else if (unit === "days") cron = `0 0 */${val} * *`;
                            setJobForm((prev) => ({ ...prev, cronUnit: unit, cron }));
                          }}
                          sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" } }}
                        >
                          <MenuItem value="minutes">Minutes</MenuItem>
                          <MenuItem value="hours">Hours</MenuItem>
                          <MenuItem value="days">Days</MenuItem>
                          <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {jobForm.cronUnit === "custom" && (
                      <TextField
                        size="small"
                        label="Cron Expression"
                        placeholder="e.g. 0 */6 * * *"
                        value={jobForm.cron}
                        onChange={(e) => setJobForm((prev) => ({ ...prev, cron: e.target.value }))}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: "monospace", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
                      />
                    )}
                    <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "rgba(255, 247, 241, 0.8)", border: "1px solid rgba(255, 132, 76, 0.10)" }}>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>Cron Expression:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#f97316", fontFamily: "monospace", mt: 0.25 }}>
                        {jobForm.cron || "—"}
                      </Typography>
                    </Box>
                  </Box>
                  <TextField
                    size="small"
                    label="Index Name"
                    value={jobForm.indexName}
                    onChange={(e) => setJobForm((prev) => ({ ...prev, indexName: e.target.value }))}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
                  />
                  <TextField
                    size="small"
                    label="Web Scrape Index Name"
                    value={jobForm.webScrapeIndexName}
                    onChange={(e) => setJobForm((prev) => ({ ...prev, webScrapeIndexName: e.target.value }))}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
                  />
                  <FormControlLabel
                    control={<Switch checked={jobForm.forceSkipScrape} onChange={(e) => setJobForm((prev) => ({ ...prev, forceSkipScrape: e.target.checked }))} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600, color: "#202531" }}>Force Skip Scrape</Typography>}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={() => { setJobFormOpen(false); setEditingJob(null); }} sx={{ color: "#6b7280" }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSaveJob}
                  disabled={savingJob || !jobForm.cron.trim()}
                  sx={{
                    background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                    "&:hover": { background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" },
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  {savingJob ? <CircularProgress size={16} color="inherit" /> : editingJob ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Job Confirmation Dialog */}
            <Dialog
              open={!!deleteJobConfirm}
              onClose={() => setDeleteJobConfirm(null)}
              PaperProps={{ sx: { borderRadius: "16px", border: "1px solid rgba(239, 68, 68, 0.15)", p: 1 } }}
            >
              <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>Delete Job</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Are you sure you want to delete this job?
                </Typography>
                <Typography variant="body2" sx={{ color: "#202531", fontWeight: 600, mt: 1, wordBreak: "break-all" }}>
                  {deleteJobConfirm?.id}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setDeleteJobConfirm(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => handleDeleteJob(deleteJobConfirm?.id)}
                  sx={{ backgroundColor: "#ef4444", "&:hover": { backgroundColor: "#dc2626" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={jobHistoryOpen}
              onClose={() => setJobHistoryOpen(false)}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
                  boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
                },
              }}
            >
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>
                  Job History — {jobHistoryData.jobId}
                </Typography>
                <IconButton onClick={() => setJobHistoryOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 2, px: 3, pb: 3 }}>
                {jobHistoryLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={32} sx={{ color: "#f97316" }} />
                  </Box>
                ) : jobHistoryData.runs.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 4 }}>No history found for this job.</Typography>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: "#6b7280", mb: 1, display: "block" }}>
                      Total runs: {jobHistoryData.totalCount}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {jobHistoryData.runs.map((run, idx) => (
                        <Paper key={run.jobId || idx} elevation={0} sx={{ p: 1.5, borderRadius: "12px", border: "1px solid rgba(255, 132, 76, 0.08)", "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.5)" } }}>
                          <Box sx={{ display: "grid", gridTemplateColumns: "90px 1fr auto 100px", gap: 1, alignItems: "center" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: run.state === "Succeeded" ? "#16a34a" : run.state === "Failed" ? "#dc2626" : "#6b7280" }}>
                              {run.state}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#202531" }}>
                              {run.completedAt ? new Date(run.completedAt).toLocaleString() : "—"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#9ca3af", fontStyle: "italic" }}>
                              {run.state === "Processing" ? "running..." : run.completedAt ? (() => {
                                const diff = Date.now() - new Date(run.completedAt).getTime();
                                const mins = Math.floor(diff / 60000);
                                if (mins < 1) return "finished just now";
                                if (mins < 60) return `finished ${mins} min ago`;
                                const hrs = Math.floor(mins / 60);
                                if (hrs < 24) return `finished ${hrs} hr${hrs > 1 ? "s" : ""} ago`;
                                const days = Math.floor(hrs / 24);
                                return `finished ${days} day${days > 1 ? "s" : ""} ago`;
                              })() : ""}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#6b7280", textAlign: "right" }}>
                              {run.durationMs != null ? `${(run.durationMs / 1000).toFixed(1)}s` : "—"}
                            </Typography>
                          </Box>
                          {run.error && (
                            <Typography variant="caption" sx={{ color: "#dc2626", mt: 0.75, display: "block", wordBreak: "break-all" }}>
                              {run.error}
                            </Typography>
                          )}
                          {run.arguments && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(255, 132, 76, 0.06)" }}>
                              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                                {Object.entries(run.arguments).filter(([, v]) => v).map(([key, value]) => (
                                  <Typography key={key} variant="caption" sx={{ color: "#6b7280" }}>
                                    {key}: <strong style={{ color: "#202531" }}>{value}</strong>
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                    {jobHistoryData.totalCount > 20 && (
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          disabled={jobHistoryPage <= 1}
                          onClick={() => handleJobHistory(jobHistoryData.jobId, jobHistoryPage - 1)}
                          sx={{ color: "#f97316", textTransform: "none" }}
                        >
                          Previous
                        </Button>
                        <Typography variant="caption" sx={{ alignSelf: "center", color: "#6b7280" }}>
                          Page {jobHistoryPage} of {Math.ceil(jobHistoryData.totalCount / 20)}
                        </Typography>
                        <Button
                          size="small"
                          disabled={jobHistoryPage >= Math.ceil(jobHistoryData.totalCount / 20)}
                          onClick={() => handleJobHistory(jobHistoryData.jobId, jobHistoryPage + 1)}
                          sx={{ color: "#f97316", textTransform: "none" }}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </DialogContent>
            </Dialog>
          </SectionView>
        );
      case "integration":
        return (
          <SectionView title="Integration" description="Configure third-party integrations for this tenant." icon={activeSectionMeta.icon} count={activeSectionMeta.count}>
            {/* Reviews Integration */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: "18px",
                border: "1px solid rgba(255, 132, 76, 0.12)",
                backgroundColor: "rgba(255,255,255,0.92)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "11px",
                    background: "linear-gradient(180deg, rgba(255,181,141,0.30) 0%, rgba(255,139,71,0.18) 100%)",
                    border: "1px solid rgba(255, 132, 76, 0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#f97316",
                    flexShrink: 0,
                  }}
                >
                  <RateReviewOutlinedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#202531", letterSpacing: "-0.02em" }}>
                    Reviews Integration
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.82rem" }}>
                    Connect a review platform to enrich product data with ratings and reviews.
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="review-provider-label">Provider</InputLabel>
                    <Select
                      labelId="review-provider-label"
                      value={reviewProvider}
                      label="Provider"
                      onChange={(e) => {
                        setReviewProvider(e.target.value);
                        setReviewApiKey("");
                        setReviewFeedback(null);
                      }}
                    >
                      {REVIEW_PROVIDERS.map((p) => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Key Name"
                    value={reviewKeyName}
                    disabled
                    inputProps={{ autoComplete: "off", spellCheck: false }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={6}>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={reviewApiKey}
                    onChange={(e) => {
                      setReviewApiKey(e.target.value);
                      setReviewFeedback(null);
                    }}
                    placeholder={reviewProvider ? `Enter ${reviewProvider} API key` : "Select a provider first"}
                    disabled={!reviewProvider}
                    inputProps={{ autoComplete: "off", spellCheck: false }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <TextField
                    fullWidth
                    label="Refresh Frequency (hours)"
                    type="number"
                    value={reviewRefreshFrequency}
                    onChange={(e) => {
                      setReviewRefreshFrequency(e.target.value);
                      setReviewFeedback(null);
                    }}
                    inputProps={{ min: 1, autoComplete: "off" }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <TextField
                    fullWidth
                    label="Max Reviews per Product"
                    type="number"
                    value={reviewMaxPerProduct}
                    onChange={(e) => {
                      setReviewMaxPerProduct(e.target.value);
                      setReviewFeedback(null);
                    }}
                    inputProps={{ min: 1, autoComplete: "off" }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSaveReviewKeys}
                    disabled={savingReviewKeys || !reviewProvider || !reviewApiKey.trim()}
                    startIcon={savingReviewKeys ? <CircularProgress size={15} sx={{ color: "rgba(255,255,255,0.8)" }} /> : null}
                    sx={primaryButtonSx}
                  >
                    {savingReviewKeys ? "Saving…" : "Save"}
                  </Button>
                </Grid>
              </Grid>

              {reviewFeedback && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 1.1,
                    borderRadius: "10px",
                    backgroundColor: reviewFeedback.type === "success"
                      ? "rgba(34, 197, 94, 0.08)"
                      : "rgba(239, 68, 68, 0.08)",
                    border: `1px solid ${reviewFeedback.type === "success"
                      ? "rgba(34, 197, 94, 0.22)"
                      : "rgba(239, 68, 68, 0.22)"}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: reviewFeedback.type === "success" ? "#15803d" : "#b91c1c",
                    }}
                  >
                    {reviewFeedback.message}
                  </Typography>
                </Box>
              )}
            </Paper>
          </SectionView>
        );
      case "assets":
        return (
          <SectionView title="Asset Management" description="Upload and manage static files for this tenant. Assets can be public (direct URL) or private (download only via CMS)." icon={activeSectionMeta.icon} count={assetsTotalCount}>
            {/* Hidden file inputs */}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <input
              ref={uploadInputRef}
              type="file"
              hidden
              accept={ASSET_FILE_ACCEPT}
              onChange={(e) => { if (e.target.files[0]) handleAssetUpload(e.target.files[0]); }}
            />
            <input
              ref={replaceInputRef}
              type="file"
              hidden
              accept={ASSET_FILE_ACCEPT}
              onChange={(e) => { if (e.target.files[0]) handleAssetReplace(e.target.files[0]); }}
            />

            {/* Upload bar */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#202531", fontSize: "0.95rem" }}>
                  Files
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.8rem" }}>
                  Max 50 MB · Images, PDF, CSV, JSON, XLS, XLSX, DOC, DOCX
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                {/* View toggle */}
                <ToggleButtonGroup
                  value={assetsViewMode}
                  exclusive
                  onChange={(_, val) => { if (val) setAssetsViewMode(val); }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      border: "1px solid rgba(255, 132, 76, 0.22)",
                      color: "#9ca3af",
                      borderRadius: "10px !important",
                      px: 1,
                      "&.Mui-selected": {
                        backgroundColor: "rgba(255, 241, 233, 0.96)",
                        color: "#f97316",
                        borderColor: "rgba(249, 115, 22, 0.35)",
                      },
                      "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.8)" },
                    },
                  }}
                >
                  <ToggleButton value="list" aria-label="list view">
                    <Tooltip title="List view">
                      <ViewListOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="grid" aria-label="grid view">
                    <Tooltip title="Grid view">
                      <GridViewOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title="Refresh files">
                  <IconButton
                    onClick={() => fetchAssets(assetsPage)}
                    disabled={assetsLoading}
                    sx={{
                      border: "1px solid rgba(255, 132, 76, 0.22)",
                      borderRadius: "10px",
                      color: assetsLoading ? "#d1d5db" : "#9ca3af",
                      "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.8)", color: "#f97316", borderColor: "rgba(249, 115, 22, 0.35)" },
                    }}
                  >
                    <RefreshOutlinedIcon fontSize="small" sx={{ transition: "transform 0.4s", ...(assetsLoading && { animation: "spin 1s linear infinite" }) }} />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  startIcon={uploadingAsset
                    ? <CircularProgress size={15} sx={{ color: "rgba(255,255,255,0.8)" }} />
                    : <CloudUploadOutlinedIcon />}
                  disabled={uploadingAsset}
                  onClick={(e) => setUploadPopoverAnchor(e.currentTarget)}
                  sx={primaryButtonSx}
                >
                  {uploadingAsset ? "Uploading…" : "Upload File"}
                </Button>
                <Popover
                  open={Boolean(uploadPopoverAnchor)}
                  anchorEl={uploadPopoverAnchor}
                  onClose={() => setUploadPopoverAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      borderRadius: "14px",
                      border: "1px solid rgba(255, 132, 76, 0.15)",
                      boxShadow: "0 12px 32px rgba(236, 123, 55, 0.14)",
                      p: 2,
                      minWidth: 200,
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#202531", fontSize: "0.85rem", mb: 1.25 }}>
                    Upload visibility
                  </Typography>
                  <ToggleButtonGroup
                    value={uploadVisibility}
                    exclusive
                    onChange={(_, val) => { if (val) setUploadVisibility(val); }}
                    size="small"
                    fullWidth
                    sx={{
                      mb: 1.5,
                      "& .MuiToggleButton-root": {
                        border: "1px solid rgba(255, 132, 76, 0.22)",
                        color: "#6b7280",
                        borderRadius: "10px !important",
                        px: 1.5,
                        py: 0.5,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(255, 241, 233, 0.96)",
                          color: "#f97316",
                          borderColor: "rgba(249, 115, 22, 0.35)",
                        },
                        "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.8)" },
                      },
                    }}
                  >
                    <ToggleButton value="public">
                      <PublicOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} /> Public
                    </ToggleButton>
                    <ToggleButton value="private">
                      <LockOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} /> Private
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<CloudUploadOutlinedIcon />}
                    onClick={() => { setUploadPopoverAnchor(null); uploadInputRef.current?.click(); }}
                    sx={{
                      ...primaryButtonSx,
                      borderRadius: "10px",
                    }}
                  >
                    Choose File
                  </Button>
                </Popover>
              </Box>
            </Box>

            {/* Visibility filter + upload visibility */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <ToggleButtonGroup
                value={assetsVisibilityFilter}
                exclusive
                onChange={(_, val) => { if (val) setAssetsVisibilityFilter(val); }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "1px solid rgba(255, 132, 76, 0.22)",
                    color: "#6b7280",
                    borderRadius: "10px !important",
                    px: 1.5,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 241, 233, 0.96)",
                      color: "#f97316",
                      borderColor: "rgba(249, 115, 22, 0.35)",
                    },
                    "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.8)" },
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="public">
                  <PublicOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} /> Public
                </ToggleButton>
                <ToggleButton value="private">
                  <LockOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} /> Private
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Feedback banner */}
            {assetsFeedback && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.75,
                  py: 1.1,
                  borderRadius: "10px",
                  mb: 2,
                  backgroundColor: assetsFeedback.type === "success"
                    ? "rgba(34, 197, 94, 0.08)"
                    : "rgba(239, 68, 68, 0.08)",
                  border: `1px solid ${assetsFeedback.type === "success"
                    ? "rgba(34, 197, 94, 0.22)"
                    : "rgba(239, 68, 68, 0.22)"}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: assetsFeedback.type === "success" ? "#15803d" : "#b91c1c",
                    flex: 1,
                  }}
                >
                  {assetsFeedback.message}
                </Typography>
                <IconButton size="small" onClick={() => setAssetsFeedback(null)} sx={{ color: "#9ca3af" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Loading */}
            {assetsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress size={28} sx={{ color: "#f97316" }} />
              </Box>
            )}

            {/* Empty state */}
            {!assetsLoading && assetsList.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 7,
                  gap: 1.5,
                  color: "#9ca3af",
                  border: "2px dashed rgba(255, 132, 76, 0.18)",
                  borderRadius: "16px",
                  backgroundColor: "rgba(255, 247, 241, 0.4)",
                }}
              >
                <FolderOutlinedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  No assets uploaded yet
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>
                  Click "Upload File" to add your first asset.
                </Typography>
              </Box>
            )}

            {/* Assets list / grid */}
            {!assetsLoading && assetsList.length > 0 && (
              <>
                {/* ── List view ── */}
                {assetsViewMode === "list" && (
                  <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(255, 132, 76, 0.10)", overflow: "hidden" }}>
                    {/* Table header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 0.7fr 0.7fr 0.7fr 0.7fr auto",
                        px: 2,
                        py: 1.25,
                        backgroundColor: "rgba(255, 247, 241, 0.7)",
                        borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
                      }}
                    >
                      {["File", "Size", "Type", "Visibility", "Last Modified", ""].map((h) => (
                        <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>

                    {/* Rows */}
                    {assetsList.map((asset, idx) => (
                      <Box
                        key={asset.name}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2fr 0.7fr 0.7fr 0.7fr 0.7fr auto",
                          px: 2,
                          py: 1.5,
                          alignItems: "center",
                          borderBottom: idx < assetsList.length - 1 ? "1px solid rgba(255, 132, 76, 0.07)" : "none",
                          "&:hover": { backgroundColor: "rgba(255, 247, 241, 0.5)" },
                          transition: "background 0.15s",
                        }}
                      >
                        {/* Name */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                          <div
                            style={{ flexShrink: 0, width: 32, height: 42, cursor: "pointer", borderRadius: 4, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
                            onClick={() => handleOpenAssetPreview(asset)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleOpenAssetPreview(asset);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`Preview ${asset.name}`}
                          >
                            {isImageType(asset.contentType) && asset.url ? (
                              <img src={asset.url} alt={asset.name} style={{ width: 32, height: 42, objectFit: "cover", display: "block" }} />
                            ) : (
                              getAssetIcon(asset.name, asset.contentType, 32)
                            )}
                          </div>
                          <Box sx={{ minWidth: 0 }}>
                            <Tooltip title={asset.url || asset.name} placement="top">
                              <Typography
                                {...(asset.url ? { component: "a", href: asset.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                                sx={{
                                  fontWeight: 600,
                                  color: "#202531",
                                  fontSize: "0.875rem",
                                  textDecoration: "none",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                  ...(asset.url && { "&:hover": { color: "#f97316", textDecoration: "underline" }, cursor: "pointer" }),
                                }}
                              >
                                {asset.name}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Size */}
                        <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.82rem" }}>
                          {formatAssetSize(asset.size)}
                        </Typography>

                        {/* Content type */}
                        <Tooltip title={asset.contentType || ""}>
                          <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {asset.contentType?.split("/")[1] || asset.contentType || "—"}
                          </Typography>
                        </Tooltip>

                        {/* Visibility */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {asset.visibility?.toLowerCase() === "private" ? (
                            <Chip icon={<LockOutlinedIcon sx={{ fontSize: "14px !important" }} />} label="Private" size="small" sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#dc2626", border: "1px solid rgba(239, 68, 68, 0.20)", "& .MuiChip-icon": { color: "#dc2626" } }} />
                          ) : (
                            <Chip icon={<PublicOutlinedIcon sx={{ fontSize: "14px !important" }} />} label="Public" size="small" sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#16a34a", border: "1px solid rgba(34, 197, 94, 0.20)", "& .MuiChip-icon": { color: "#16a34a" } }} />
                          )}
                        </Box>

                        {/* Last modified */}
                        <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.82rem" }}>
                          {asset.lastModified ? new Date(asset.lastModified).toLocaleDateString() : "—"}
                        </Typography>

                        {/* Actions */}
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <Tooltip title={asset.visibility?.toLowerCase() === "public" ? "Make private" : "Make public"}>
                            <IconButton
                              size="small"
                              disabled={togglingVisibility === asset.name}
                              onClick={() => handleToggleVisibility(asset)}
                              sx={{ color: "#7c3aed", "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.08)" } }}
                            >
                              {togglingVisibility === asset.name
                                ? <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
                                : asset.visibility?.toLowerCase() === "public" ? <LockOutlinedIcon sx={{ fontSize: 18 }} /> : <PublicOutlinedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Replace file">
                            <IconButton
                              size="small"
                              disabled={replacingAsset && replaceTarget?.name === asset.name}
                              onClick={() => { setReplaceTarget(asset); replaceInputRef.current?.click(); }}
                              sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}
                            >
                              {replacingAsset && replaceTarget?.name === asset.name
                                ? <CircularProgress size={16} sx={{ color: "#f97316" }} />
                                : <SwapHorizOutlinedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download file">
                            <IconButton
                              size="small"
                              onClick={asset.visibility?.toLowerCase() === "private" ? () => {
                                axiosInstance.get(`/assets/${tenantDetails?.id}/${encodeURIComponent(asset.name)}/download`, { responseType: "blob" })
                                  .then((res) => {
                                    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
                                    const link = document.createElement("a");
                                    link.href = blobUrl;
                                    link.setAttribute("download", asset.name);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(blobUrl);
                                  })
                                  .catch(() => setAssetsFeedback({ type: "error", message: "Download failed." }));
                              } : undefined}
                              {...(asset.visibility?.toLowerCase() !== "private" && { component: "a", href: asset.url, download: asset.name, target: "_blank", rel: "noopener noreferrer" })}
                              sx={{ color: "#2563eb", "&:hover": { backgroundColor: "rgba(37,99,235,0.08)" } }}
                            >
                              <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={!asset.url ? "No public URL (private)" : copiedAssetName === asset.name ? "Copied!" : "Copy URL"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyAssetUrl(asset)}
                                disabled={!asset.url}
                                sx={{ color: copiedAssetName === asset.name ? "#16a34a" : "#6b7280", "&:hover": { backgroundColor: "rgba(107,114,128,0.08)" } }}
                              >
                                <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete file">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteAssetConfirm(asset)}
                              sx={{ color: "#ef4444", "&:hover": { backgroundColor: "rgba(239,68,68,0.08)" } }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* ── Grid view ── */}
                {assetsViewMode === "grid" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {assetsList.map((asset) => (
                      <Paper
                        key={asset.name}
                        elevation={0}
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid rgba(255, 132, 76, 0.12)",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          p: 1.75,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                          transition: "box-shadow 0.15s, border-color 0.15s",
                          "&:hover": {
                            borderColor: "rgba(249, 115, 22, 0.30)",
                            boxShadow: "0 8px 24px rgba(236, 123, 55, 0.10)",
                          },
                        }}
                      >
                        {/* Icon / thumbnail */}
                        <Box
                          onClick={() => handleOpenAssetPreview(asset)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleOpenAssetPreview(asset);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Preview ${asset.name}`}
                          sx={{
                            width: "100%",
                            height: 90,
                            borderRadius: "10px",
                            backgroundColor: "rgba(255, 247, 241, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            cursor: "pointer",
                            overflow: "hidden",
                            transition: "opacity 0.15s",
                            "&:hover": { opacity: 0.88 },
                          }}
                        >
                        {isImageType(asset.contentType) && asset.url ? (
                            <Box
                              component="img"
                              src={asset.url}
                              alt={asset.name}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            getAssetIcon(asset.name, asset.contentType, 40)
                          )}
                        </Box>
                        {/* Name */}
                        <Tooltip title={asset.name} placement="top">
                          <Typography
                            {...(asset.url ? { component: "a", href: asset.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.78rem",
                              color: "#202531",
                              textDecoration: "none",
                              textAlign: "center",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              ...(asset.url && { "&:hover": { color: "#f97316" }, cursor: "pointer" }),
                            }}
                          >
                            {asset.name}
                          </Typography>
                        </Tooltip>

                        {/* Visibility badge */}
                        {asset.visibility?.toLowerCase() === "private" ? (
                          <Chip icon={<LockOutlinedIcon sx={{ fontSize: "12px !important" }} />} label="Private" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#dc2626", border: "1px solid rgba(239, 68, 68, 0.20)", "& .MuiChip-icon": { color: "#dc2626", ml: "4px !important" } }} />
                        ) : (
                          <Chip icon={<PublicOutlinedIcon sx={{ fontSize: "12px !important" }} />} label="Public" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#16a34a", border: "1px solid rgba(34, 197, 94, 0.20)", "& .MuiChip-icon": { color: "#16a34a", ml: "4px !important" } }} />
                        )}

                        {/* Meta */}
                        <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.72rem" }}>
                          {formatAssetSize(asset.size)}
                        </Typography>

                        {/* Actions */}
                        <Box sx={{ display: "flex", gap: 0.5, mt: "auto" }}>
                          <Tooltip title={asset.visibility?.toLowerCase() === "public" ? "Make private" : "Make public"}>
                            <IconButton
                              size="small"
                              disabled={togglingVisibility === asset.name}
                              onClick={() => handleToggleVisibility(asset)}
                              sx={{ color: "#7c3aed", "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.08)" } }}
                            >
                              {togglingVisibility === asset.name
                                ? <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
                                : asset.visibility?.toLowerCase() === "public" ? <LockOutlinedIcon sx={{ fontSize: 16 }} /> : <PublicOutlinedIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Replace file">
                            <IconButton
                              size="small"
                              disabled={replacingAsset && replaceTarget?.name === asset.name}
                              onClick={() => { setReplaceTarget(asset); replaceInputRef.current?.click(); }}
                              sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}
                            >
                              {replacingAsset && replaceTarget?.name === asset.name
                                ? <CircularProgress size={14} sx={{ color: "#f97316" }} />
                                : <SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download file">
                            <IconButton
                              size="small"
                              onClick={asset.visibility?.toLowerCase() === "private" ? () => {
                                axiosInstance.get(`/assets/${tenantDetails?.id}/${encodeURIComponent(asset.name)}/download`, { responseType: "blob" })
                                  .then((res) => {
                                    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
                                    const link = document.createElement("a");
                                    link.href = blobUrl;
                                    link.setAttribute("download", asset.name);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(blobUrl);
                                  })
                                  .catch(() => setAssetsFeedback({ type: "error", message: "Download failed." }));
                              } : undefined}
                              {...(asset.visibility?.toLowerCase() !== "private" && { component: "a", href: asset.url, download: asset.name, target: "_blank", rel: "noopener noreferrer" })}
                              sx={{ color: "#2563eb", "&:hover": { backgroundColor: "rgba(37,99,235,0.08)" } }}
                            >
                              <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={!asset.url ? "No public URL (private)" : copiedAssetName === asset.name ? "Copied!" : "Copy URL"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyAssetUrl(asset)}
                                disabled={!asset.url}
                                sx={{ color: copiedAssetName === asset.name ? "#16a34a" : "#6b7280", "&:hover": { backgroundColor: "rgba(107,114,128,0.08)" } }}
                              >
                                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete file">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteAssetConfirm(asset)}
                              sx={{ color: "#ef4444", "&:hover": { backgroundColor: "rgba(239,68,68,0.08)" } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

                {/* Pagination */}
                {assetsTotalCount > ASSETS_PAGE_SIZE && (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mt: 2 }}>
                    <Button
                      size="small"
                      disabled={assetsPage === 0}
                      onClick={() => fetchAssets(assetsPage - 1)}
                      sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      Page {assetsPage + 1} of {Math.ceil(assetsTotalCount / ASSETS_PAGE_SIZE)}
                    </Typography>
                    <Button
                      size="small"
                      disabled={assetsPage >= Math.ceil(assetsTotalCount / ASSETS_PAGE_SIZE) - 1}
                      onClick={() => fetchAssets(assetsPage + 1)}
                      sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Asset preview dialog */}
            <Dialog
              open={!!previewAsset}
              onClose={handleCloseAssetPreview}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 132, 76, 0.12)",
                  background: "linear-gradient(180deg, rgba(255,248,243,0.98), rgba(255,255,255,0.96))",
                  boxShadow: "0 30px 70px rgba(236,123,55,0.16)",
                  overflow: "hidden",
                },
              }}
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,132,76,0.10)",
                  py: 1.5,
                  px: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <div style={{ flexShrink: 0 }}>
                    {previewAsset && getAssetIcon(previewAsset.name, previewAsset.contentType, 28)}
                  </div>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#202531",
                      fontSize: "1rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {previewAsset?.name}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseAssetPreview}
                  aria-label="Close asset preview"
                  sx={{ color: "#6b7280", flexShrink: 0, "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 0 }}>
                {previewLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress size={32} sx={{ color: "#f97316" }} />
                  </Box>
                )}

                {!previewLoading && previewAsset && (() => {
                  const { contentType, url, size } = previewAsset;

                  // File too large — download only
                  if ((size || 0) > PREVIEW_SIZE_LIMIT) {
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                        <div style={{ flexShrink: 0 }}>
                          {getAssetIcon(previewAsset.name, contentType, 56)}
                        </div>
                        <Typography sx={{ color: "#202531", fontWeight: 700, fontSize: "1rem" }}>
                          File is too large to preview
                        </Typography>
                        <Typography sx={{ color: "#6b7280", fontWeight: 500, fontSize: "0.875rem" }}>
                          {formatAssetSize(size)} · Files over 3 MB cannot be previewed.
                        </Typography>
                        <Button
                          variant="contained"
                          component="a"
                          href={url}
                          download={previewAsset.name}
                          rel="noopener noreferrer"
                          startIcon={<DownloadOutlinedIcon />}
                          sx={primaryButtonSx}
                        >
                          Download
                        </Button>
                      </Box>
                    );
                  }

                  // Image
                  if (isImageType(contentType)) {
                    return (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2, backgroundColor: "#f8f8f8", minHeight: 300 }}>
                        <Box
                          component="img"
                          src={url}
                          alt={previewAsset.name}
                          sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}
                        />
                      </Box>
                    );
                  }

                  // PDF
                  if (isPdfType(contentType)) {
                    return (
                      <Box sx={{ height: "70vh" }}>
                        <iframe
                          src={url}
                          title={previewAsset.name}
                          width="100%"
                          height="100%"
                          style={{ border: "none", display: "block" }}
                        />
                      </Box>
                    );
                  }

                  // DOCX documents are downloaded through the authenticated API and rendered in memory.
                  if (isDocxType(previewAsset.name, contentType)) {
                    if (previewWordError) {
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, px: 3, gap: 2, textAlign: "center" }}>
                          <div style={{ flexShrink: 0 }}>
                            {getAssetIcon(previewAsset.name, contentType, 56)}
                          </div>
                          <Typography sx={{ color: "#202531", fontWeight: 700 }}>
                            Unable to preview this DOCX document
                          </Typography>
                          <Typography sx={{ color: "#6b7280", fontWeight: 500, maxWidth: 520 }}>
                            {previewWordError}
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ height: "70vh", overflow: "auto", backgroundColor: "#eef1f5", p: { xs: 1.5, sm: 3 } }}>
                        <Paper
                          elevation={0}
                          sx={{
                            boxSizing: "border-box",
                            width: "min(100%, 816px)",
                            minHeight: "100%",
                            mx: "auto",
                            px: { xs: 2.5, sm: 7 },
                            py: { xs: 3, sm: 6 },
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 12px rgba(15, 23, 42, 0.12)",
                          }}
                        >
                          {(previewWordContent || []).length === 0 ? (
                            <Typography sx={{ color: "#6b7280", textAlign: "center" }}>
                              This document has no readable text content.
                            </Typography>
                          ) : (
                            previewWordContent.map((block, blockIndex) => block.type === "table" ? (
                              <Box key={blockIndex} sx={{ overflowX: "auto", my: 2 }}>
                                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                                  <tbody>
                                    {block.rows.map((row, rowIndex) => (
                                      <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                          <td key={cellIndex} style={{ border: "1px solid #cbd5e1", padding: "8px", whiteSpace: "pre-wrap", verticalAlign: "top" }}>
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                            ) : (
                              <Typography
                                key={blockIndex}
                                component="p"
                                sx={{ m: 0, minHeight: "1.5em", color: "#202531", lineHeight: 1.6, whiteSpace: "pre-wrap" }}
                              >
                                {block.text || "\u00a0"}
                              </Typography>
                            ))
                          )}
                        </Paper>
                      </Box>
                    );
                  }

                  // Only public DOC files can currently use Office's web viewer.
                  if (isLegacyDocType(previewAsset.name, contentType)) {
                    if (previewWordError) {
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, px: 3, gap: 2, textAlign: "center" }}>
                          <div style={{ flexShrink: 0 }}>
                            {getAssetIcon(previewAsset.name, contentType, 56)}
                          </div>
                          <Typography sx={{ color: "#202531", fontWeight: 700 }}>
                            Unable to preview this DOC document
                          </Typography>
                          <Typography sx={{ color: "#6b7280", fontWeight: 500, maxWidth: 460 }}>
                            {previewWordError}
                          </Typography>
                        </Box>
                      );
                    }

                    const legacyDocPreviewUrl = getPublicOfficePreviewUrl(previewAsset);

                    return (
                      <Box sx={{ height: "70vh", backgroundColor: "#f8f8f8" }}>
                        <iframe
                          src={legacyDocPreviewUrl}
                          title={previewAsset.name}
                          width="100%"
                          height="100%"
                          style={{ border: "none", display: "block" }}
                        />
                      </Box>
                    );
                  }

                  // CSV — spreadsheet-style viewer
                  if (contentType === "text/csv") {
                    const parseCSV = (raw = "") => {
                      const lines = raw.split(/\r?\n/).filter((l) => l.trim());
                      return lines.map((line) => {
                        const cols = [];
                        let cur = "", inQuote = false;
                        for (let i = 0; i < line.length; i++) {
                          const ch = line[i];
                          if (ch === '"') { inQuote = !inQuote; }
                          else if (ch === "," && !inQuote) { cols.push(cur); cur = ""; }
                          else { cur += ch; }
                        }
                        cols.push(cur);
                        return cols;
                      });
                    };
                    const rows = parseCSV(previewContent || "");
                    const headers = rows[0] || [];
                    const dataRows = rows.slice(1);
                    const needle = previewCsvSearch.toLowerCase();
                    const filteredRows = needle
                      ? dataRows.filter((row) => row.some((cell) => cell.toLowerCase().includes(needle)))
                      : dataRows;

                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", height: "70vh" }}>
                        {/* Search bar */}
                        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(255,132,76,0.10)", backgroundColor: "rgba(255,247,241,0.5)" }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Search in CSV…"
                            value={previewCsvSearch}
                            onChange={(e) => setPreviewCsvSearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 18, color: "#f97316" }} />
                                </InputAdornment>
                              ),
                              endAdornment: previewCsvSearch ? (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setPreviewCsvSearch("")}>
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backgroundColor: "#fff",
                                "& fieldset": { borderColor: "rgba(255,132,76,0.20)" },
                                "&:hover fieldset": { borderColor: "#f97316" },
                                "&.Mui-focused fieldset": { borderColor: "#f97316", borderWidth: "1px" },
                              },
                            }}
                          />
                          {needle && (
                            <Typography variant="caption" sx={{ color: "#6b7280", mt: 0.5, display: "block" }}>
                              {filteredRows.length} of {dataRows.length} rows match
                            </Typography>
                          )}
                        </Box>

                        {/* Table */}
                        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
                          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.82rem", fontFamily: "system-ui, sans-serif" }}>
                            <thead>
                              <tr>
                                {/* Row number header */}
                                <th style={{ position: "sticky", top: 0, left: 0, zIndex: 3, backgroundColor: "#fff7ed", borderBottom: "2px solid rgba(255,132,76,0.20)", borderRight: "1px solid rgba(255,132,76,0.12)", padding: "6px 10px", color: "#9ca3af", fontWeight: 700, fontSize: "0.7rem", textAlign: "center", minWidth: 40 }}>
                                  #
                                </th>
                                {headers.map((h, i) => (
                                  <th
                                    key={i}
                                    style={{ position: "sticky", top: 0, zIndex: 2, backgroundColor: "#fff7ed", borderBottom: "2px solid rgba(255,132,76,0.20)", borderRight: "1px solid rgba(255,132,76,0.08)", padding: "6px 12px", color: "#202531", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}
                                  >
                                    {h || `Col ${i + 1}`}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredRows.length === 0 ? (
                                <tr>
                                  <td colSpan={headers.length + 1} style={{ padding: "32px", textAlign: "center", color: "#9ca3af" }}>
                                    No rows match your search.
                                  </td>
                                </tr>
                              ) : (
                                filteredRows.map((row, ri) => (
                                  <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? "#fff" : "rgba(255,247,241,0.45)" }}>
                                    {/* Row number */}
                                    <td style={{ borderRight: "1px solid rgba(255,132,76,0.12)", borderBottom: "1px solid rgba(255,132,76,0.07)", padding: "5px 10px", color: "#9ca3af", fontSize: "0.72rem", textAlign: "center", fontWeight: 600, whiteSpace: "nowrap" }}>
                                      {ri + 1}
                                    </td>
                                    {headers.map((_, ci) => {
                                      const cell = row[ci] ?? "";
                                      const highlighted = needle && cell.toLowerCase().includes(needle);
                                      return (
                                        <td
                                          key={ci}
                                          style={{ borderRight: "1px solid rgba(255,132,76,0.07)", borderBottom: "1px solid rgba(255,132,76,0.07)", padding: "5px 12px", color: "#374151", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", backgroundColor: highlighted ? "rgba(255,251,196,0.7)" : undefined }}
                                          title={cell}
                                        >
                                          {cell}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </Box>

                        {/* Footer count */}
                        <Box sx={{ px: 2, py: 0.75, borderTop: "1px solid rgba(255,132,76,0.10)", backgroundColor: "rgba(255,247,241,0.5)" }}>
                          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                            {headers.length} columns · {dataRows.length} rows total
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }

                  // JSON / TXT — plain text
                  if (isTextType(contentType)) {
                    return (
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 2.5,
                          fontFamily: "monospace",
                          fontSize: "0.82rem",
                          color: "#202531",
                          backgroundColor: "#fafafa",
                          overflowX: "auto",
                          overflowY: "auto",
                          maxHeight: "70vh",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {previewContent ?? ""}
                      </Box>
                    );
                  }

                  // Unsupported — offer download link
                  return (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
                      <div style={{ flexShrink: 0 }}>
                        {getAssetIcon(previewAsset.name, contentType, 56)}
                      </div>
                      <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>
                        Preview not available for this file type.
                      </Typography>
                      <Button
                        variant="contained"
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={primaryButtonSx}
                      >
                        Open / Download
                      </Button>
                    </Box>
                  );
                })()}
              </DialogContent>

              {/* Footer with meta + open link */}
              {!previewLoading && previewAsset && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    py: 1.25,
                    borderTop: "1px solid rgba(255,132,76,0.10)",
                    backgroundColor: "rgba(255,247,241,0.5)",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2.5, alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                      {formatAssetSize(previewAsset.size)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                      {previewAsset.contentType}
                    </Typography>
                    {previewAsset.lastModified && (
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                        Modified {new Date(previewAsset.lastModified).toLocaleDateString()}
                      </Typography>
                    )}
                    {previewAsset.visibility && (
                      <Chip
                        icon={previewAsset.visibility?.toLowerCase() === "private" ? <LockOutlinedIcon sx={{ fontSize: "13px !important" }} /> : <PublicOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                        label={previewAsset.visibility?.toLowerCase() === "private" ? "Private" : "Public"}
                        size="small"
                        sx={{
                          height: 20, fontSize: "0.72rem", fontWeight: 600,
                          ...(previewAsset.visibility?.toLowerCase() === "private"
                            ? { backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#dc2626", border: "1px solid rgba(239, 68, 68, 0.18)", "& .MuiChip-icon": { color: "#dc2626" } }
                            : { backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#16a34a", border: "1px solid rgba(34, 197, 94, 0.18)", "& .MuiChip-icon": { color: "#16a34a" } }),
                        }}
                      />
                    )}
                  </Box>
                  {previewAsset.url && (
                    <Button
                      size="small"
                      component="a"
                      href={previewAsset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#f97316", textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
                    >
                      Open in new tab ↗
                    </Button>
                  )}
                </Box>
              )}
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
              open={!!deleteAssetConfirm}
              onClose={() => setDeleteAssetConfirm(null)}
              PaperProps={{ sx: { borderRadius: "16px", border: "1px solid rgba(239, 68, 68, 0.15)", p: 1 } }}
            >
              <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>Delete Asset</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Are you sure you want to permanently delete this file? This action cannot be undone.
                </Typography>
                <Typography variant="body2" sx={{ color: "#202531", fontWeight: 600, mt: 1, wordBreak: "break-all" }}>
                  {deleteAssetConfirm?.name}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setDeleteAssetConfirm(null)} sx={{ color: "#6b7280", textTransform: "none" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!!deletingAsset}
                  onClick={() => handleAssetDelete(deleteAssetConfirm)}
                  startIcon={deletingAsset ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : null}
                  sx={{ backgroundColor: "#ef4444", "&:hover": { backgroundColor: "#dc2626" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                >
                  {deletingAsset ? "Deleting…" : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>
          </SectionView>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: isConfigurationMapCompact ? "88px minmax(0, 1fr)" : "280px minmax(0, 1fr)",
          },
          gap: 2.5,
          alignItems: "start",
          transition: "grid-template-columns 0.2s ease",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...sectionPaperSx,
            p: isConfigurationMapCompact ? 1 : 2,
            position: { md: "sticky" },
            top: { md: 16 },
            overflow: "hidden",
            transition: "padding 0.2s ease",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={isConfigurationMapCompact ? "center" : "space-between"}
            sx={{ mb: isConfigurationMapCompact ? 1 : 0 }}
          >
            {!isConfigurationMapCompact && (
              <Typography sx={{ fontWeight: 800, color: "#202531", fontSize: "1.3rem" }}>
                Configuration Map
              </Typography>
            )}
            <Tooltip title={isConfigurationMapCompact ? "Expand configuration map" : "Collapse configuration map"}>
              <IconButton
                size="small"
                aria-label={isConfigurationMapCompact ? "Expand configuration map" : "Collapse configuration map"}
                onClick={() => setIsConfigurationMapCompact((compact) => !compact)}
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  color: "#64748b",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  backgroundColor: "rgba(255, 247, 241, 0.72)",
                  "&:hover": { backgroundColor: "rgba(255, 241, 233, 0.96)", color: "#f97316" },
                }}
              >
                {isConfigurationMapCompact ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          {!isConfigurationMapCompact && (
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5, mb: 2 }}>
              Select one section to show it in the main configuration area.
            </Typography>
          )}
          <Stack spacing={isConfigurationMapCompact ? 0.5 : 0.75}>
            {visibleSectionGroups.map((group) => {
              const isExpanded = expandedGroups.includes(group.groupId);
              const isSearching = searchQuery.trim().length > 0;
              const groupHasMatch = group.items.some((s) => sectionsWithMatches.has(s.id));
              const groupHasActive = group.items.some((s) => s.id === activeSection);

              return (
                <Box key={group.groupId}>
                  <Tooltip title={isConfigurationMapCompact ? group.groupTitle : ""} placement="right">
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={group.groupTitle}
                      onClick={() => toggleGroup(group.groupId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleGroup(group.groupId);
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isConfigurationMapCompact ? "center" : "flex-start",
                        gap: 1,
                        px: isConfigurationMapCompact ? 0.75 : 1.5,
                        py: 1,
                        borderRadius: "14px",
                        cursor: "pointer",
                        opacity: isSearching && !groupHasMatch ? 0.4 : 1,
                        backgroundColor: groupHasActive ? "rgba(255, 241, 233, 0.6)" : "transparent",
                        outline: "none",
                        "&:hover, &:focus-visible": {
                          backgroundColor: "rgba(255, 247, 241, 0.8)",
                        },
                        transition: "background-color 0.15s, opacity 0.2s",
                      }}
                    >
                      <group.groupIcon sx={{ fontSize: isConfigurationMapCompact ? 21 : 18, color: groupHasActive ? "#f97316" : "#94a3b8", flexShrink: 0 }} />
                      {!isConfigurationMapCompact && (
                        <>
                          <Typography sx={{ fontWeight: 800, color: groupHasActive ? "#f97316" : "#202531", fontSize: "0.85rem", flex: 1, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                            {group.groupTitle}
                          </Typography>
                          <ExpandMoreIcon
                            sx={{
                              fontSize: 18,
                              color: "#94a3b8",
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </Tooltip>
                  <Box
                    sx={{
                      overflow: "hidden",
                      maxHeight: isExpanded ? `${group.items.length * 56}px` : "0px",
                      transition: "max-height 0.25s ease",
                    }}
                  >
                    <Stack spacing={0.5} sx={{ pl: isConfigurationMapCompact ? 0 : 1.5, pt: 0.5 }}>
                      {group.items.map((section) => {
                        const hasMatch = sectionsWithMatches.has(section.id);
                        const isActive = activeSection === section.id;

                        return (
                          <Tooltip key={section.id} title={isConfigurationMapCompact ? section.title : ""} placement="right">
                            <Box
                              role="button"
                              tabIndex={hasMatch || !isSearching ? 0 : -1}
                              aria-label={section.title}
                              onClick={() => (hasMatch || !isSearching) ? handleSectionClick(section.id, group.groupId) : undefined}
                              onKeyDown={(event) => {
                                if ((event.key === "Enter" || event.key === " ") && (hasMatch || !isSearching)) {
                                  event.preventDefault();
                                  handleSectionClick(section.id, group.groupId);
                                }
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isConfigurationMapCompact ? "center" : "flex-start",
                                gap: 1,
                                px: isConfigurationMapCompact ? 0.75 : 1.25,
                                py: isConfigurationMapCompact ? 1 : 0.9,
                                borderRadius: "12px",
                                cursor: hasMatch || !isSearching ? "pointer" : "default",
                                backgroundColor: isActive ? "rgba(255, 241, 233, 0.96)" : "transparent",
                                borderLeft: isActive ? "3px solid #f97316" : "3px solid transparent",
                                opacity: isSearching && !hasMatch ? 0.4 : 1,
                                outline: "none",
                                transition: "all 0.15s ease",
                                "&:hover, &:focus-visible": (hasMatch || !isSearching) ? {
                                  backgroundColor: isActive ? "rgba(255, 241, 233, 0.96)" : "rgba(255, 247, 241, 0.72)",
                                } : {},
                              }}
                            >
                              <section.icon sx={{ fontSize: isConfigurationMapCompact ? 20 : 17, color: isActive ? "#f97316" : "#94a3b8", flexShrink: 0 }} />
                              {!isConfigurationMapCompact && (
                                <>
                                  <Typography sx={{ fontWeight: isActive ? 700 : 600, color: isActive ? "#f97316" : "#374151", fontSize: "0.88rem", flex: 1 }}>
                                    {section.title}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    {isSearching && hasMatch && (
                                      <Chip
                                        label={`${(sectionFieldsMap[section.id] || []).filter((f) => matchesSearch(f.label, f.value, searchQuery)).length}`}
                                        size="small"
                                        sx={{ ...metaChipSx, backgroundColor: "rgba(249, 115, 22, 0.15)", color: "#ea580c", fontSize: "0.65rem", height: 18, minWidth: 0 }}
                                      />
                                    )}
                                    {section.showCount !== false && <Chip label={section.count} size="small" sx={{ ...metaChipSx, fontSize: "0.65rem", height: 18, minWidth: 0 }} />}
                                  </Stack>
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ ...sectionPaperSx, p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <TextField
                fullWidth
                placeholder="Search configs, flags, fields, or JSON keys"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "#94a3b8", mr: 1 }} />,
                }}
                sx={inputSx}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Version ${tenantDetails?.version ?? "-"}`} sx={metaChipSx} />
              </Stack>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            {renderActiveSection()}

            {!["order-tracking", "merchandising", "taxonomy"].includes(activeSection) && (
              <Paper
                elevation={0}
                sx={{
                  ...sectionPaperSx,
                  p: 2,
                  position: { md: "sticky" },
                  bottom: 16,
                  zIndex: 2,
                  backgroundColor: "rgba(255,255,255,0.96)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: "#202531", fontSize: "1.15rem" }}>
                      Footer Actions
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                      Save the updated tenant JSON or preview the widget.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon />}
                      onClick={() => setPreviewOpen(true)}
                      sx={neutralButtonSx}
                    >
                      Preview Widget
                    </Button>
                    {activeSection !== "enrichment" && (
                      <Button
                        variant="contained"
                        onClick={handleSaveTenant}
                        disabled={!!jsonError || !!tenantJsonError || savingTenant || savingNightlyProductUpdate}
                        sx={primaryButtonSx}
                      >
                        {savingTenant ? "Saving..." : "Save Changes"}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Box>
      </Box>

      <PreviewWidgetDialog open={previewOpen} onClose={() => setPreviewOpen(false)} tenantDetails={tenantDetails} />

      <Dialog
        open={ruleDetailOpen}
        onClose={() => setRuleDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
              Rule Detail
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
              Inspect the rule, edit the script, and validate the result.
            </Typography>
          </Box>
          <IconButton
            onClick={() => setRuleDetailOpen(false)}
            sx={{ width: 40, height: 40, borderRadius: "14px", color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, backgroundColor: "transparent", borderTop: 0, borderBottom: 0 }}>
          {ruleDetailData && (
            <Box sx={{ p: 2.25, borderRadius: "22px", border: "1px solid rgba(255, 132, 76, 0.10)", backgroundColor: "rgba(255,255,255,0.82)", boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)" }}>
              <Box display="flex" gap={3} flexWrap="wrap" mb={1.5}>
                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Type</Typography>
                  <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.type}</Typography>
                </Box>
                {ruleDetailData.rule.fieldName && (
                  <Box sx={{ minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Field Name</Typography>
                    <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.fieldName}</Typography>
                  </Box>
                )}
              </Box>
              <EnrichmentPreviewSourceEditor ruleDetailData={ruleDetailData} setRuleDetailData={setRuleDetailData} disabled={ruleDetailRetesting} />
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Script</Typography>
                <TextField
                  label="Rule script"
                  fullWidth
                  multiline
                  minRows={3}
                  value={ruleDetailData.rule.script || ""}
                  onChange={(e) =>
                    setRuleDetailData((prev) => ({
                      ...prev,
                      rule: { ...prev.rule, script: e.target.value },
                    }))
                  }
                  sx={{
                    mt: 0.75,
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      fontFamily: "monospace",
                      fontSize: "0.88rem",
                    },
                  }}
                />
              </Box>
              <Box mb={1.5} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={ruleDetailRetesting || !!ruleDetailData.sourceFieldError}
                  onClick={async () => {
                    const result = await handleRetestRule(ruleDetailData.rule, getEnrichmentPreviewSourceFieldEdit(ruleDetailData));
                    setRuleDetailData((prev) => ({ ...prev, result }));
                  }}
                  sx={neutralButtonSx}
                >
                  {ruleDetailRetesting ? <CircularProgress size={14} /> : "Re-test"}
                </Button>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Result</Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: ruleDetailData.result?.error ? "rgba(254, 242, 242, 0.96)" : "rgba(240, 253, 244, 0.96)",
                    color: ruleDetailData.result?.error ? "#dc2626" : "#15803d",
                    p: 1.5,
                    borderRadius: 2,
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    border: "1px solid",
                    borderColor: ruleDetailData.result?.error ? "rgba(248, 113, 113, 0.30)" : "rgba(74, 222, 128, 0.30)",
                    mt: 0.75,
                  }}
                >
                  {ruleDetailData.result?.error || ruleDetailData.result?.value || "(empty)"}
                </Typography>
               </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Global Asset Picker ── */}
      <AssetPickerDialog
        open={!!assetPickerTarget}
        onClose={() => setAssetPickerTarget(null)}
        onSelect={(asset) => {
          if (assetPickerTarget?.path) {
            updateValue(assetPickerTarget.path, asset.url);
          }
          setAssetPickerTarget(null);
        }}
        tenantId={tenantDetails?.id}
        title={assetPickerTarget?.title || "Select a Tenant Asset"}
        accept={assetPickerTarget?.accept}
      />
    </>
  );
};

export default TenantManagementModernLayout;
